// Language configuration with both code and name for UI usage
export const LANGUAGE_CONFIG = {
  ar: { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  bn: { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch' },
  en: { code: 'en', name: 'English', nativeName: 'English' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français' },
  he: { code: 'he', name: 'Hebrew', nativeName: 'עברית' },
  hi: { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  it: { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  ja: { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  ko: { code: 'ko', name: 'Korean', nativeName: '한국어' },
  pt: { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  ru: { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  tr: { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  vi: { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  zh: { code: 'zh', name: 'Chinese (Mandarin)', nativeName: '中文（普通话）' },
} as const;

// Type for language codes
export type LanguageCode = keyof typeof LANGUAGE_CONFIG;

// Type for language configuration
export type LanguageConfig = (typeof LANGUAGE_CONFIG)[LanguageCode];

// Helper functions for UI usage
export const getLanguageName = (code: LanguageCode): string =>
  LANGUAGE_CONFIG[code].name;
export const getLanguageCode = (code: LanguageCode): string =>
  LANGUAGE_CONFIG[code].code;
export const getAllLanguages = (): LanguageConfig[] =>
  Object.values(LANGUAGE_CONFIG);
export const getSupportedLanguageCodes = (): LanguageCode[] =>
  Object.keys(LANGUAGE_CONFIG) as LanguageCode[];

// Keep the original array for backward compatibility
export const SUPPORTED_LANGUAGE_CODES = Object.keys(
  LANGUAGE_CONFIG,
) as LanguageCode[];

export interface HcMessages {
  languageId: string;

  call: {
    greeting: string;
    prompt: string;
    timeout: string;
    sms: string;
    smsPrompt: string;
    meaningsKeyword: {
      ok: string[];
      notOk: string[];
      needHelp: string[];
    }
    receiptConfirmationKeywords: string[];
  };
  timeout: {
    noResponse: string;
    retry: string;
  };
  responseMessages: {
    ok: string;
    notOk: string;
    emergency: string;
    noAnswer: string;
    errorGeneric: string;
    goodbye: string;
    clarification: string;
    smsClarification: string;
    notUnderstoodFinal: string;
  };
  notificationMessages: {
    ok: {
      smsTemplate: (loved: string) => string;
      emailTemplate: (loved: string) => string;
    };
    emergency: {
      greeting: string;
      messageTemplate: (loved: string) => string;
      prompt: string;
      timeout: string;
      smsTemplate: (loved: string) => string;
      emailTemplate: (loved: string) => string;
    };
    /** NEED_HELP due to no response during call (possible incapacitation) - same flow as emergency, different wording. Optional; fallback to English in getNotificationMessages. */
    emergencyNoResponse?: {
      greeting: string;
      messageTemplate: (loved: string) => string;
      prompt: string;
      timeout: string;
      smsTemplate: (loved: string) => string;
      /** When attemptsSummary is provided (retry history), it is appended after the main message. */
      emailTemplate: (loved: string, attemptsSummary?: string) => string;
    };
    notOk: {
      greeting: string;
      messageTemplate: (loved: string) => string;
      prompt: string;
      timeout: string;
      smsTemplate: (loved: string) => string;
      emailTemplate: (loved: string) => string;
      retrySmsTemplate: (loved: string) => string; // NEW: For reminder SMS after timeout
      escalationEmailTemplate: (loved: string) => string; // NEW: For final escalation email
    };
    noAnswer: {
      greeting: string;
      messageTemplate: (loved: string) => string;
      prompt: string;
      timeout: string;
      smsTemplate: (loved: string) => string;
      emailTemplate: (loved: string) => string;
    };
    /** Loved one did not answer; automatic retry is scheduled (informational to contacts). */
    noAnswerRetryPending: {
      smsTemplate: (loved: string, retryInMinutes: number) => string;
      emailTemplate: (loved: string, retryInMinutes: number) => string;
    };
    notUnderstood: {
      smsTemplate: (loved: string) => string;
      emailTemplate: (loved: string) => string;
    };
  };
}

// Interface for response messages returned by HcStandardResponses.get()
export interface HcResponseMessages {
  ok: string;
  notOk: string;
  emergency: string;
  noAnswer: string;
  errorGeneric: string;
  goodbye: string;
  clarification: string;
  smsClarification: string;
  notUnderstoodFinal: string;
}

// Interface for voice instructions returned by HcVoice methods
export interface HcVoiceInstructions {
  greeting: string;
  message: string;
  prompt: string;
  timeout: string;
  languageId: string;
  sms?: string; // SMS version of the message
}

export class HcMessageStrings {
  private static messages: Record<string, HcMessages> = {
    ar: {
      languageId: 'ar',
      call: {
        greeting: `مرحباً، هذه مكالمة فحص صحي.`,
        prompt: `اضغط 1 إذا كنت بخيراً، 5 إذا لم تكن بخيراً و 9 للمساعدة أو قل "بخير"، "لست بخيراً" أو "مساعدة".`,
        timeout: `لم نتلق أي رد. سنتصل بك مرة أخرى.`,
        sms: `مرحباً، هذه رسالة فحص لمعرفة كيف حالك. يرجى الرد على هذه الرسالة.`,
        smsPrompt: `رد "بخير" أو "لست بخير" أو "مساعدة" حسب حالتك. شكراً لك!`,
        meaningsKeyword: {
          ok: ["بخير", "أنا بخير", "أنا جيد", "كل شيء بخير", "أشعر بالراحة", "أنا بصحة جيدة", "أنا آمن", "أنا على ما يرام", "حالتي جيدة", "أنا في حالة جيدة"],
          notOk: ["لست بخير", "أنا لست بخير", "أنا لست جيد", "أنا مريض", "لدي مشاكل", "أشعر بالسوء", "أنا لست على ما يرام", "حالتي ليست جيدة", "أشعر بالمرض", "أحتاج إلى رعاية"],
          needHelp: ["مساعدة", "أحتاج إلى مساعدة", "ساعدني", "أنا في خطر", "حالة طوارئ", "أحتاج مساعدة عاجلة", "النجدة", "أحتاج مساعدة فورية", "ساعدوني", "أنا بحاجة إلى مساعدة"]
        },
        receiptConfirmationKeywords: ["استلمت الرسالة", "حسناً", "أؤكد", "فهمت", "نعم", "موافق", "سمعت"]
      },
      timeout: {
        noResponse: `لم نتلق أي رد. سنتواصل مع جهات الاتصال الطارئة الخاصة بك للتحقق من أنك بخير.`,
        retry: `لم نتلق أي رد. سنتصل بك مرة أخرى.`,
      },
      responseMessages: {
        ok: `شكرًا لتأكيدك أنك بخير. سننهي هذه المكالمة الآن. اعتنِ بنفسك.`,
        notOk: `شكرًا لإبلاغنا. سنتواصل فورًا مع جهات الاتصال الطارئة الخاصة بك.`,
        emergency: `شكرًا لإبلاغنا. سنتواصل فورًا مع جهات الاتصال الطارئة الخاصة بك.`,
        noAnswer: `مع السلامة.`,
        errorGeneric: `عذرًا، حدث خطأ أثناء معالجة ردك.`,
        goodbye: `مع السلامة.`,
        clarification: `لم أفهم إجابتك. يرجى الضغط على 1 إذا كنت بخيراً، 5 إذا لم تكن بخيراً أو 9 للمساعدة. يمكنك أيضًا أن تقول "بخير"، "لست بخيراً" أو "مساعدة".`,
        smsClarification: `لم أفهم ردك. يرجى الرد الآن. اكتب "بخير" إذا كنت بخيراً، "لست بخيراً" إذا لم تكن بخيراً، أو "مساعدة" إذا كنت بحاجة إلى مساعدة.`,
        notUnderstoodFinal: `لم نفهم ردك. سنتواصل مع جهات الاتصال الطارئة الخاصة بك للاطمئنان عليك.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `أخبار سارة! ${loved} أكمل فحصه الصحي وهو بخير.`,
          emailTemplate: (loved: string) =>
            `أخبار سارة! ${loved} أكمل فحصه الصحي وهو بخير. هذه رسالة إعلامية من Omi-Check.`,
        },
        emergency: {
          greeting: `مرحباً، هذه إشعار طوارئ من Omi-Check.`,
          messageTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأنه قد تكون هناك حالة طوارئ مع ${loved}. يرجى الضغط على 1 لتأكيد استلام هذه الرسالة.`,
          prompt: `يرجى الرد الآن بالضغط على 1 لتأكيد الاستلام.`,
          timeout: `لم نتلق رداً. سنواصل محاولة الوصول إليك وإلى جهات الاتصال الطارئة الأخرى.`,
          smsTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأنه قد تكون هناك حالة طوارئ مع ${loved}. يرجى الرد بـ "حسناً" لتأكيد الاستلام.`,
          emailTemplate: (loved: string) =>
            `إشعار طوارئ من Omi-Check: قد تكون هناك حالة طوارئ مع ${loved}. يرجى التحقق منهم فوراً. هذه رسالة إعلامية من Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `مرحباً، هذه إشعار عاجل من Omi-Check.`,
          messageTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأننا لم نتمكن من الحصول على رد من ${loved} أثناء مكالمة التحقق. قد يكون غير قادر على الرد. يرجى الضغط على 1 لتأكيد استلام هذه الرسالة والتحقق منهم في أقرب وقت ممكن.`,
          prompt: `يرجى الرد الآن بالضغط على 1 لتأكيد الاستلام.`,
          timeout: `لم نتلق رداً. سنواصل محاولة الوصول إليك وإلى جهات الاتصال الطارئة الأخرى.`,
          smsTemplate: (loved: string) =>
            `لم نتمكن من الحصول على رد من ${loved} أثناء مكالمة التحقق. قد يكون غير قادر على الرد. يرجى التحقق منهم في أقرب وقت ممكن والرد "حسناً" لتأكيد قراءة هذه الرسالة.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `إشعار عاجل من Omi-Check: لم نتمكن من الحصول على رد من ${loved} أثناء مكالمة التحقق. قد يكون غير قادر على الرد. يرجى التحقق منهم في أقرب وقت ممكن. هذه رسالة إعلامية من Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `مرحباً، هذه إشعار من Omi-Check.`,
          messageTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأن ${loved} أشار إلى أنه لا يشعر بالراحة. يرجى الضغط على 1 لتأكيد استلام هذه الرسالة.`,
          prompt: `يرجى الرد الآن بالضغط على 1 لتأكيد الاستلام.`,
          timeout: `لم نتلق رداً. سنواصل محاولة الوصول إليك وإلى جهات الاتصال الأخرى.`,
          smsTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأن ${loved} أشار إلى أنه لا يشعر بالراحة. يرجى الرد بـ "حسناً" لتأكيد الاستلام.`,
          emailTemplate: (loved: string) =>
            `إشعار من Omi-Check: ${loved} أشار إلى أنه لا يشعر بالراحة. يرجى التحقق منهم عند الإمكان. هذه رسالة إعلامية من Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `تذكير: إشعار من Omi-Check: ${loved} أشار إلى أنه لا يشعر بالراحة. يرجى الرد بـ "حسناً" لتأكيد الاستلام.`,
          escalationEmailTemplate: (loved: string) =>
            `عاجل: حاولنا الوصول إلى جميع جهات الاتصال الطارئة لـ ${loved} دون نجاح. لم يرد أحد على إشعاراتنا. يرجى التحقق منهم فوراً. هذه إشعار تصعيد من Omi-Check.`,
        },
        noAnswer: {
          greeting: `مرحباً، هذه إشعار من Omi-Check.`,
          messageTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأننا لم نتمكن من الوصول إلى ${loved} لفحصهم الصحي المجدول. يرجى الضغط على 1 لتأكيد استلام هذه الرسالة.`,
          prompt: `يرجى الرد الآن بالضغط على 1 لتأكيد الاستلام.`,
          timeout: `لم نتلق رداً. سنواصل محاولة الوصول إليك وإلى جهات الاتصال الأخرى.`,
          smsTemplate: (loved: string) =>
            `نتصل بك لإعلامك بأننا لم نتمكن من الوصول إلى ${loved} لفحصهم الصحي المجدول. يرجى الرد بـ "حسناً" لتأكيد الاستلام.`,
          emailTemplate: (loved: string) =>
            `إشعار من Omi-Check: لم نتمكن من الوصول إلى ${loved} لفحصهم الصحي المجدول. يرجى التحقق منهم عند الإمكان. هذه رسالة إعلامية من Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} لم يرد على المكالمة. سنعيد المحاولة خلال ${retryInMinutes} دقيقة.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `إشعار من Omi-Check: ${loved} لم يرد على الفحص الصحي المجدول. سنعيد المحاولة تلقائياً خلال ${retryInMinutes} دقيقة. هذه رسالة إعلامية من Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `لم نتمكن من فهم رد ${loved} أثناء فحصهم الصحي. يرجى التحقق منهم للتأكد من أنهم بخير، من Omi-Check.`,
          emailTemplate: (loved: string) =>
            `عزيزك ${loved}، أجاب على الهاتف أثناء مكالمتهم المجدولة لكننا لم نتمكن من فهم إجابتهم اللفظية. يرجى التحقق منهم للتأكد من أنهم بخير.`,
        },
      },
    },
    bn: {
      languageId: 'bn',
      call: {
        greeting: `হ্যালো, এটি একটি স্বাস্থ্য পরীক্ষার কল।`,
        prompt: `আপনি ভাল থাকলে ১ চাপুন, ভাল না থাকলে ৫ চাপুন এবং সাহায্যের জন্য ৯ চাপুন বা বলুন "ভাল", "ভাল নেই" বা "সাহায্য"।`,
        timeout: `আমরা কোনো উত্তর পাইনি। আমরা আপনাকে আবার কল করব।`,
        sms: `হাই, এটি একটি চেক-আপ বার্তা যাতে আমরা জানতে পারি আপনি কেমন আছেন। অনুগ্রহ করে এই বার্তার উত্তর দিন।`,
        smsPrompt: `"ভাল", "ভাল নেই" বা "সাহায্য" উত্তর দিন আপনার অবস্থা অনুযায়ী। ধন্যবাদ!`,
        meaningsKeyword: {
          ok: ["ভাল", "আমি ভাল আছি", "আমি ঠিক আছি", "সব ঠিক আছে", "আমি ভাল বোধ করছি", "আমি সুস্থ আছি", "আমি নিরাপদ", "আমার অবস্থা ভাল", "আমি স্বাচ্ছন্দ্য বোধ করছি", "আমার স্বাস্থ্য ভালো"],
          notOk: ["ভাল নেই", "আমি ভাল নেই", "আমি অসুস্থ", "আমার সমস্যা আছে", "আমি খারাপ বোধ করছি", "আমার স্বাস্থ্য ভালো নয়", "আমি অসুস্থ বোধ করছি", "আমার সমস্যা হয়েছে", "আমি ভালো নই", "আমার সাহায্য প্রয়োজন"],
          needHelp: ["সাহায্য", "আমার সাহায্য দরকার", "আমাকে সাহায্য করুন", "আমার সাহায্যের প্রয়োজন", "আমি বিপদে আছি", "জরুরি অবস্থা", "আমার জরুরি সাহায্য দরকার", "আমাকে বাঁচান", "আমার সাহায্য চাই", "জরুরি সাহায্য"]
        },
        receiptConfirmationKeywords: ["পেয়েছি", "ঠিক আছে", "নিশ্চিত করছি", "বুঝেছি", "হ্যাঁ", "নিশ্চিত", "শুনেছি"]
      },
      timeout: {
        noResponse: `আমরা কোনো উত্তর পাইনি। আমরা নিশ্চিত করতে আপনার জরুরি যোগাযোগের সাথে যোগাযোগ করব যে আপনি ভালো আছেন।`,
        retry: `আমরা কোনো উত্তর পাইনি। আমরা আপনাকে আবার কল করব।`,
      },
      responseMessages: {
        ok: `ধন্যবাদ, আপনি নিশ্চিত করেছেন যে আপনি ভালো আছেন। আমরা এখন কলটি শেষ করবো। নিজের খেয়াল রাখুন।`,
        notOk: `জানানোর জন্য ধন্যবাদ। আমরা অবিলম্বে আপনার জরুরি পরিচিতিদের সাথে যোগাযোগ করবো।`,
        emergency: `জানানোর জন্য ধন্যবাদ। আমরা অবিলম্বে আপনার জরুরি পরিচিতিদের সাথে যোগাযোগ করবো।`,
        noAnswer: `বিদায়।`,
        errorGeneric: `দুঃখিত, আপনার উত্তর প্রক্রিয়া করার সময় একটি ত্রুটি ঘটেছে।`,
        goodbye: `বিদায়।`,
        clarification: `আমি আপনার উত্তর বুঝতে পারিনি। অনুগ্রহ করে 1 চাপুন যদি আপনি ভাল থাকেন, 5 যদি আপনি ভাল না থাকেন, বা 9 যদি আপনার সাহায্যের প্রয়োজন হয়। আপনি "ভাল", "ভাল নেই", বা "সাহায্য" বলতে পারেন।`,
        smsClarification: `আমি আপনার উত্তর বুঝতে পারিনি। অনুগ্রহ করে এখন উত্তর দিন। "ভাল" লিখুন যদি আপনি ভাল থাকেন, "ভাল নেই" লিখুন যদি আপনি ভাল না থাকেন, বা "সাহায্য" লিখুন যদি আপনার সাহায্যের প্রয়োজন হয়।`,
        notUnderstoodFinal: `আমরা আপনার উত্তর বুঝতে পারিনি। আমরা আপনার জরুরি পরিচিতিদের সাথে যোগাযোগ করবো আপনার খোঁজ নেওয়ার জন্য।`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `সুসংবাদ! ${loved} তাদের স্বাস্থ্য পরীক্ষা সম্পন্ন করেছেন এবং ভাল আছেন।`,
          emailTemplate: (loved: string) =>
            `সুসংবাদ! ${loved} তাদের স্বাস্থ্য পরীক্ষা সম্পন্ন করেছেন এবং ভাল আছেন। এটি Omi-Check থেকে একটি তথ্যমূলক আপডেট।`,
        },
        emergency: {
          greeting: `হ্যালো, এটি Omi-Check থেকে একটি জরুরি বিজ্ঞপ্তি।`,
          messageTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে ${loved} এর সাথে একটি জরুরি পরিস্থিতি হতে পারে। অনুগ্রহ করে এই বার্তাটি পেয়েছেন তা নিশ্চিত করতে 1 চাপুন।`,
          prompt: `অনুগ্রহ করে এখন 1 চেপে প্রাপ্তি নিশ্চিত করুন।`,
          timeout: `আমরা কোনো উত্তর পাইনি। আমরা আপনাকে এবং অন্যান্য জরুরি যোগাযোগের সাথে যোগাযোগ করতে থাকব।`,
          smsTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে ${loved} এর সাথে একটি জরুরি পরিস্থিতি হতে পারে। অনুগ্রহ করে "ঠিক আছে" লিখে নিশ্চিত করুন।`,
          emailTemplate: (loved: string) =>
            `Omi-Check জরুরি বিজ্ঞপ্তি: ${loved} এর সাথে একটি জরুরি পরিস্থিতি হতে পারে। অনুগ্রহ করে তাদের সাথে অবিলম্বে যোগাযোগ করুন। এটি Omi-Check থেকে একটি তথ্যমূলক আপডেট।`,
        },
        emergencyNoResponse: {
          greeting: `হ্যালো, এটি Omi-Check থেকে একটি জরুরি বিজ্ঞপ্তি।`,
          messageTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে আমরা ${loved} এর চেক-ইন কলের সময় কোনো উত্তর পাইনি। তারা উত্তর দিতে অক্ষম হতে পারে। অনুগ্রহ করে এই বার্তা পেয়েছেন তা নিশ্চিত করতে 1 চাপুন এবং যত তাড়াতাড়ি সম্ভব তাদের খোঁজ নিন।`,
          prompt: `অনুগ্রহ করে এখন 1 চেপে প্রাপ্তি নিশ্চিত করুন।`,
          timeout: `আমরা কোনো উত্তর পাইনি। আমরা আপনাকে এবং অন্যান্য জরুরি যোগাযোগের সাথে যোগাযোগ করতে থাকব।`,
          smsTemplate: (loved: string) =>
            `আমরা ${loved} এর চেক-ইন কলের সময় কোনো উত্তর পাইনি। তারা উত্তর দিতে অক্ষম হতে পারে। অনুগ্রহ করে যত তাড়াতাড়ি সম্ভব তাদের খোঁজ নিন এবং "ঠিক আছে" লিখে নিশ্চিত করুন।`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check জরুরি বিজ্ঞপ্তি: আমরা ${loved} এর চেক-ইন কলের সময় কোনো উত্তর পাইনি। তারা উত্তর দিতে অক্ষম হতে পারে। অনুগ্রহ করে যত তাড়াতাড়ি সম্ভব তাদের খোঁজ নিন। এটি Omi-Check থেকে একটি তথ্যমূলক আপডেট।`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `হ্যালো, এটি Omi-Check থেকে একটি বিজ্ঞপ্তি।`,
          messageTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে ${loved} ভাল লাগছে না। অনুগ্রহ করে এই বার্তাটি পেয়েছেন তা নিশ্চিত করতে 1 চাপুন।`,
          prompt: `অনুগ্রহ করে এখন 1 চেপে প্রাপ্তি নিশ্চিত করুন।`,
          timeout: `আমরা কোনো উত্তর পাইনি। আমরা আপনাকে এবং অন্যান্য যোগাযোগের সাথে যোগাযোগ করতে থাকব।`,
          smsTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে ${loved} ভাল লাগছে না। অনুগ্রহ করে "ঠিক আছে" লিখে নিশ্চিত করুন।`,
          emailTemplate: (loved: string) =>
            `Omi-Check বিজ্ঞপ্তি: ${loved} ভাল লাগছে না। অনুগ্রহ করে যখন সম্ভব তাদের সাথে যোগাযোগ করুন। এটি Omi-Check থেকে একটি তথ্যমূলক আপডেট।`,
          retrySmsTemplate: (loved: string) =>
            `স্মরণ: Omi-Check বিজ্ঞপ্তি: ${loved} ভাল লাগছে না। অনুগ্রহ করে "ঠিক আছে" লিখে নিশ্চিত করুন।`,
          escalationEmailTemplate: (loved: string) =>
            `জরুরি: আমরা ${loved} এর জন্য সমস্ত জরুরি যোগাযোগের সাথে যোগাযোগ করার চেষ্টা করেছি কিন্তু সফল হইনি। কেউ আমাদের বিজ্ঞপ্তির উত্তর দেয়নি। অনুগ্রহ করে তাদের সাথে অবিলম্বে যোগাযোগ করুন। এটি Omi-Check থেকে একটি জরুরি বিজ্ঞপ্তি।`,
        },
        noAnswer: {
          greeting: `হ্যালো, এটি Omi-Check থেকে একটি বিজ্ঞপ্তি।`,
          messageTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে আমরা ${loved} তাদের নির্ধারিত স্বাস্থ্য পরীক্ষার জন্য যোগাযোগ করতে পারিনি। অনুগ্রহ করে এই বার্তাটি পেয়েছেন তা নিশ্চিত করতে 1 চাপুন।`,
          prompt: `অনুগ্রহ করে এখন 1 চেপে প্রাপ্তি নিশ্চিত করুন।`,
          timeout: `আমরা কোনো উত্তর পাইনি। আমরা আপনাকে এবং অন্যান্য যোগাযোগের সাথে যোগাযোগ করতে থাকব।`,
          smsTemplate: (loved: string) =>
            `আমরা আপনাকে জানাতে কল করছি যে আমরা ${loved} তাদের নির্ধারিত স্বাস্থ্য পরীক্ষার জন্য যোগাযোগ করতে পারিনি। অনুগ্রহ করে "ঠিক আছে" লিখে নিশ্চিত করুন।`,
          emailTemplate: (loved: string) =>
            `Omi-Check বিজ্ঞপ্তি: আমরা ${loved} তাদের নির্ধারিত স্বাস্থ্য পরীক্ষার জন্য যোগাযোগ করতে পারিনি। অনুগ্রহ করে যখন সম্ভব তাদের সাথে যোগাযোগ করুন। এটি Omi-Check থেকে একটি তথ্যমূলক আপডেট।`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} উত্তর দেননি। আমরা ${retryInMinutes} মিনিটের মধ্যে আবার চেষ্টা করব।`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check বিজ্ঞপ্তি: ${loved} নির্ধারিত স্বাস্থ্য পরীক্ষার কলে উত্তর দেননি। আমরা ${retryInMinutes} মিনিটের মধ্যে আবার চেষ্টা করব। এটি Omi-Check থেকে একটি তথ্যমূলক আপডেট।`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `আমরা ${loved} এর স্বাস্থ্য পরীক্ষার সময় তাদের উত্তর বুঝতে পারিনি। অনুগ্রহ করে তাদের সাথে যোগাযোগ করুন যাতে তারা ভালো আছে কিনা নিশ্চিত হতে পারেন, Omi-Check থেকে।`,
          emailTemplate: (loved: string) =>
            `আপনার প্রিয়জন ${loved}, তাদের নির্ধারিত কলের সময় ফোনে উত্তর দিয়েছেন কিন্তু আমরা তাদের মৌখিক উত্তর বুঝতে পারিনি। অনুগ্রহ করে তাদের সাথে যোগাযোগ করুন যাতে তারা ভালো আছে কিনা নিশ্চিত হতে পারেন।`,
        },
      },
    },
    de: {
      languageId: 'de',
      call: {
        greeting: `Hallo, dies ist ein Gesundheitscheck-Anruf.`,
        prompt: `Drücken Sie 1, wenn es Ihnen gut geht, 5, wenn es Ihnen nicht gut geht, und 9 für Hilfe, oder sagen Sie "Ok", "nicht Ok" oder "Hilfe".`,
        timeout: `Wir haben keine Antwort erhalten. Wir werden Sie erneut anrufen.`,
        sms: `Hallo, dies ist eine Nachricht zur Überprüfung, damit wir wissen, wie es Ihnen geht. Bitte antworten Sie auf diese Nachricht.`,
        smsPrompt: `Antworten Sie "ok", "nicht ok" oder "hilfe" je nachdem, wie es Ihnen geht. Vielen Dank!`,
        meaningsKeyword: {
          ok: ["ok", "Mir geht es gut", "Ich bin okay", "Alles ist gut", "Ich fühle mich gut", "Ich bin gesund", "Ich bin sicher", "Alles in Ordnung", "Ich bin wohlauf", "Mir geht es bestens"],
          notOk: ["nicht ok", "Mir geht es nicht gut", "Ich bin nicht okay", "Ich bin krank", "Ich habe Probleme", "Ich fühle mich schlecht", "Ich bin nicht gut", "Mir geht es schlecht", "Ich fühle mich unwohl", "Ich brauche Unterstützung"],
          needHelp: ["hilfe", "Ich brauche Hilfe", "Hilf mir", "Ich bin in Gefahr", "Notfall", "Ich brauche dringend Hilfe", "Bitte helfen Sie mir", "Soforthilfe", "Ich benötige Unterstützung", "Rettung"]
        },
        receiptConfirmationKeywords: ["Verstanden", "Okay", "Bestätigt", "Ich verstehe", "Ja", "Bestätige", "Gehört", "ok", "OK"]
      },
      timeout: {
        noResponse: `Wir haben keine Antwort erhalten. Wir werden Ihre Notfallkontakte kontaktieren, um zu überprüfen, ob es Ihnen gut geht.`,
        retry: `Wir haben keine Antwort erhalten. Wir werden Sie erneut anrufen.`,
      },
      responseMessages: {
        ok: `Danke, dass Sie bestätigt haben, dass es Ihnen gut geht. Wir beenden nun dieses Gespräch. Passen Sie auf sich auf!`,
        notOk: `Danke für die Information. Wir werden umgehend Ihre Notfallkontakte kontaktieren.`,
        emergency: `Danke für die Information. Wir werden umgehend Ihre Notfallkontakte kontaktieren.`,
        noAnswer: `Auf Wiedersehen.`,
        errorGeneric: `Entschuldigung, beim Verarbeiten Ihrer Antwort ist ein Fehler aufgetreten.`,
        goodbye: `Auf Wiedersehen.`,
        clarification: `Ich habe Ihre Antwort nicht verstanden. Bitte drücken Sie 1, wenn es Ihnen gut geht, 5, wenn es Ihnen nicht gut geht, oder 9 für Hilfe. Sie können auch sagen "Ok", "nicht Ok" oder "Hilfe".`,
        smsClarification: `Ich habe Ihre Antwort nicht verstanden. Bitte antworten Sie jetzt. Schreiben Sie "Ok", wenn es Ihnen gut geht, "nicht Ok", wenn es Ihnen nicht gut geht, oder "Hilfe", wenn Sie Hilfe benötigen.`,
        notUnderstoodFinal: `Wir haben Ihre Antwort nicht verstanden. Wir werden Ihre Notfallkontakte kontaktieren, um nach Ihnen zu sehen.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Gute Nachrichten! ${loved} hat den Gesundheitscheck abgeschlossen und es geht ihm gut.`,
          emailTemplate: (loved: string) =>
            `Gute Nachrichten! ${loved} hat den Gesundheitscheck abgeschlossen und es geht ihm gut. Dies ist eine informative Aktualisierung von Omi-Check.`,
        },
        emergency: {
          greeting: `Hallo, dies ist eine Notfallbenachrichtigung von Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Wir rufen Sie an, um Sie darüber zu informieren, dass es möglicherweise eine Notfallsituation mit ${loved}. Bitte drücken Sie 1, um zu bestätigen, dass Sie diese Nachricht erhalten haben.`,
          prompt: `Bitte antworten Sie jetzt, indem Sie 1 drücken, um den Empfang zu bestätigen.`,
          timeout: `Wir haben keine Antwort erhalten. Wir werden weiterhin versuchen, Sie und andere Notfallkontakte zu erreichen.`,
          smsTemplate: (loved: string) =>
            `${loved} hat angegeben, dass Hilfe benötigt wird. Es wird dringend empfohlen, sie so schnell wie möglich zu erreichen. Bitte antworten Sie mit "OK", um zu bestätigen, dass Sie diese Nachricht gelesen haben.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Notfallbenachrichtigung: Es kann eine Notfallsituation mit ${loved} geben. Bitte überprüfen Sie sie sofort. Dies ist eine informative Aktualisierung von Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Hallo, dies ist eine dringende Benachrichtigung von Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Wir rufen Sie an, um Sie zu informieren, dass wir während des Überprüfungsanrufs keine Antwort von ${loved} erhalten haben. Möglicherweise kann er/sie nicht antworten. Bitte drücken Sie 1 zur Bestätigung und überprüfen Sie den Zustand so schnell wie möglich.`,
          prompt: `Bitte antworten Sie jetzt, indem Sie 1 drücken, um den Empfang zu bestätigen.`,
          timeout: `Wir haben keine Antwort erhalten. Wir werden weiterhin versuchen, Sie und andere Notfallkontakte zu erreichen.`,
          smsTemplate: (loved: string) =>
            `Wir haben während des Überprüfungsanrufs keine Antwort von ${loved} erhalten. Möglicherweise kann er/sie nicht antworten. Bitte überprüfen Sie den Zustand so schnell wie möglich und antworten Sie "OK" zur Bestätigung.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check dringende Benachrichtigung: Wir haben während des Überprüfungsanrufs keine Antwort von ${loved} erhalten. Möglicherweise kann er/sie nicht antworten. Bitte überprüfen Sie den Zustand so schnell wie möglich. Dies ist eine informative Aktualisierung von Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Hallo, dies ist eine Benachrichtigung von Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Wir rufen Sie an, um Sie darüber zu informieren, dass ${loved} angegeben hat, dass es ihm nicht gut geht. Bitte drücken Sie 1, um zu bestätigen, dass Sie diese Nachricht erhalten haben.`,
          prompt: `Bitte antworten Sie jetzt, indem Sie 1 drücken, um den Empfang zu bestätigen.`,
          timeout: `Wir haben keine Antwort erhalten. Wir werden weiterhin versuchen, Sie und andere Kontakte zu erreichen.`,
          smsTemplate: (loved: string) =>
            `Wir rufen Sie an, um Sie darüber zu informieren, dass ${loved} angegeben hat, dass es ihm nicht gut geht. Bitte antworten Sie mit "Okay" zur Bestätigung.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Benachrichtigung: ${loved} hat angegeben, dass es ihm nicht gut geht. Bitte überprüfen Sie sie, wenn möglich. Dies ist eine informative Aktualisierung von Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `ERINNERUNG: Omi-Check Benachrichtigung: ${loved} hat angegeben, dass es ihm nicht gut geht. Bitte antworten Sie mit "Okay" zur Bestätigung.`,
          escalationEmailTemplate: (loved: string) =>
            `DRINGEND: Wir haben versucht, alle Notfallkontakte für ${loved} zu erreichen, ohne Erfolg. Niemand hat auf unsere Benachrichtigungen geantwortet. Bitte überprüfen Sie sie sofort. Dies ist eine Eskalationsbenachrichtigung von Omi-Check.`,
        },
        noAnswer: {
          greeting: `Hallo, dies ist eine Benachrichtigung von Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Wir rufen Sie an, um Sie darüber zu informieren, dass wir ${loved} für die geplante Gesundheitsprüfung nicht erreichen konnten. Bitte drücken Sie 1, um zu bestätigen, dass Sie diese Nachricht erhalten haben.`,
          prompt: `Bitte antworten Sie jetzt, indem Sie 1 drücken, um den Empfang zu bestätigen.`,
          timeout: `Wir haben keine Antwort erhalten. Wir werden weiterhin versuchen, Sie und andere Kontakte zu erreichen.`,
          smsTemplate: (loved: string) =>
            `Wir rufen Sie an, um Sie darüber zu informieren, dass wir ${loved} für die geplante Gesundheitsprüfung nicht erreichen konnten. Bitte antworten Sie mit "Okay" zur Bestätigung.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Benachrichtigung: Wir konnten ${loved} für die geplante Gesundheitsprüfung nicht erreichen. Bitte überprüfen Sie sie, wenn möglich. Dies ist eine informative Aktualisierung von Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} hat nicht abgenommen. Wir versuchen es in ${retryInMinutes} Minuten erneut.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} hat den geplanten Anruf nicht beantwortet. Wir versuchen es automatisch in ${retryInMinutes} Minuten erneut. Informative Nachricht von Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `Wir konnten die Antwort von ${loved} während ihrer Gesundheitsprüfung nicht verstehen. Bitte überprüfen Sie sie, um sicherzustellen, dass es ihnen gut geht, von Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Ihr Angehöriger ${loved} hat während des geplanten Anrufs das Telefon beantwortet, aber wir konnten seine mündliche Antwort nicht verstehen. Bitte überprüfen Sie sie, um sicherzustellen, dass es ihnen gut geht.`,
        },
      },
    },
    en: {
      languageId: 'en',
      call: {
        greeting: `Hello, this is a health check call.`,
        prompt: `Press 1 if you're OK, 5 if you're not OK and 9 for help or say "Ok", "not Ok" or "Help".`,
        timeout: `We didn't receive any response. We will try calling you again.`,
        sms: `Hi, this is a check-up message so we know how you're doing.  Please respond to this message`,
        smsPrompt: `Respond "OK", "Not OK" or "Need Help!" depending on how you are doing. Thank you!`,
        meaningsKeyword: {
          ok: ["ok", "I am okay", "I am fine", "I am good", "I feel good", "I am healthy", "I am safe", "All is well", "I'm doing well", "Everything is fine"],
          notOk: ["not ok", "I am not okay", "I am not fine", "I am sick", "I have problems", "I feel bad", "I am not good", "I'm unwell", "I feel unwell", "I need care"],
          needHelp: ["help", "I need help", "Help me", "I need assistance", "I am in danger", "Emergency", "I need urgent help", "Please help", "I need immediate help", "SOS"]
        },
        receiptConfirmationKeywords: ["Got it", "ok", "Okay", "I confirm", "I understand", "Yes", "Confirmed", "Received"]
      },
      timeout: {
        noResponse: `We didn't receive any response. We will contact your emergency contacts to check on you.`,
        retry: `We didn't receive any response. We will try calling you again.`,
      },
      responseMessages: {
        ok: `Thank you for your response.`,
        notOk: `Thank you for letting us know you're not okay. We will contact your emergency contacts to check on you.`,
        emergency: `Thank you for letting us know you need help. We will contact your emergency contacts immediately.`,
        noAnswer: ``,
        errorGeneric: `Sorry, there was an error processing your response.`,
        goodbye: ``,
        clarification: `I did not understand your response. Please press 1 if you're OK, 5 if you're not OK, or 9 for help. You can also say "Ok", "not Ok", or "Help".`,
        smsClarification: `I did not understand your response. Please reply now. Reply "Ok" if you're ok, "not Ok" if you're not ok, or "Help" if you need help.`,
        notUnderstoodFinal: `We did not understand your response. We will contact your emergency contacts to check on you.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Good news! ${loved} has completed their health check and is doing well.`,
          emailTemplate: (loved: string) =>
            `Good news! ${loved} has completed their health check and is doing well. This is an informational update from Omi-Check.`,
        },
        emergency: {
          greeting: `Hello, this is an emergency notification from Omi-Check.`,
          messageTemplate: (loved: string) =>
            `We are calling to inform you that there may be an emergency situation with ${loved}. Please press 1 to confirm you received this message.`,
          prompt: `Please respond now by pressing 1 to confirm receipt.`,
          timeout: `We did not receive a response. We will continue trying to reach you and other emergency contacts.`,
          smsTemplate: (loved: string) =>
            `${loved} has indicated they need help. It is strongly recommended to try to reach them as quickly as possible. Please respond "OK" to confirm you have read this message.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Emergency Notification: There may be an emergency situation with ${loved}. Please check on them immediately. This is an informational update from Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Hello, this is an urgent notification from Omi-Check.`,
          messageTemplate: (loved: string) =>
            `We are calling to inform you that we could not get a response from ${loved} during their check-in call. They may be unable to respond. Please press 1 to confirm you received this message and check on them as soon as possible.`,
          prompt: `Please respond now by pressing 1 to confirm receipt.`,
          timeout: `We did not receive a response. We will continue trying to reach you and other emergency contacts.`,
          smsTemplate: (loved: string) =>
            `We could not get a response from ${loved} during their check-in call. They may be unable to respond. Please check on them as soon as possible and respond "OK" to confirm you have read this message.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check Urgent Notification: We could not get a response from ${loved} during their check-in call. They may be unable to respond. Please check on them as soon as possible. This is an informational update from Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Hello, this is a notification from Omi-Check.`,
          messageTemplate: (loved: string) =>
            `We are calling to inform you that ${loved} indicated they are not doing well. Please press 1 to confirm you received this message.`,
          prompt: `Please respond now by pressing 1 to confirm receipt.`,
          timeout: `We did not receive a response. We will continue trying to reach you and other contacts.`,
          smsTemplate: (loved: string) =>
            `We are calling to inform you that ${loved} indicated they are not doing well. Please respond "ok" to acknowledge receipt.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Notification: ${loved} indicated they are not doing well. Please check on them when possible. This is an informational update from Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `REMINDER: Omi-Check notification: ${loved} indicated they are not doing well. Please respond "ok" to acknowledge receipt.`,
          escalationEmailTemplate: (loved: string) =>
            `URGENT: We tried to reach all emergency contacts for ${loved} without success. No one responded to our notifications. Please check on them immediately. This is an escalation notification from Omi-Check.`,
        },
        noAnswer: {
          greeting: `Hello, this is a notification from Omi-Check.`,
          messageTemplate: (loved: string) =>
            `We are calling to inform you that we were unable to reach ${loved} for their scheduled health check. Please press 1 to confirm you received this message.`,
          prompt: `Please respond now by pressing 1 to confirm receipt.`,
          timeout: `We did not receive a response. We will continue trying to reach you and other contacts.`,
          smsTemplate: (loved: string) =>
            `We are calling to inform you that we were unable to reach ${loved} for their scheduled health check. Please respond "ok" to acknowledge receipt.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Notification: We were unable to reach ${loved} for their scheduled health check. Please check on them when possible. This is an informational update from Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} did not answer. We will try again in ${retryInMinutes} minutes.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check notification: ${loved} did not answer their scheduled health check. We will try again automatically in ${retryInMinutes} minutes. This is an informational update from Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `We were unable to understand ${loved}'s response during their health check. Please check on them to ensure they are okay, from Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Your loved one ${loved}, answered the phone during their scheduled call but we were unable to understand their verbal answer. Please check on them to ensure they are OK.`,
        },
      },
    },
    es: {
      languageId: 'es',
      call: {
        greeting: `Hola, esta es una llamada de control de salud.`,
        prompt: `Presione 1 si está bien, 5 si no está bien y 9 para ayuda o diga "Bien", "no bien" o "Ayuda".`,
        timeout: `No hemos recibido ninguna respuesta. Le llamaremos de nuevo.`,
        sms: `Hola, este es un mensaje de verificación para saber cómo estás. Por favor responde a este mensaje.`,
        smsPrompt: `Responda "bien", "no bien" o "ayuda" según cómo se sienta. ¡Gracias!`,
        meaningsKeyword: {
          ok: ["bien", "Estoy bien", "Todo está bien", "Me siento bien", "Estoy sano", "Estoy seguro", "Todo correcto", "Me encuentro bien", "Estoy en buen estado", "Todo va bien"],
          notOk: ["no bien", "No estoy bien", "Estoy enfermo", "Tengo problemas", "Me siento mal", "No me siento bien", "Estoy mal", "Me encuentro mal", "Necesito atención", "No estoy en buen estado"],
          needHelp: ["ayuda", "Necesito ayuda", "Ayúdame", "Estoy en peligro", "Emergencia", "Necesito ayuda urgente", "Por favor ayúdame", "Socorro", "Necesito asistencia", "Ayuda inmediata"]
        },
        receiptConfirmationKeywords: ["Entendido", "De acuerdo", "Confirmo", "Comprendo", "Sí", "Confirmado", "Recibido", "ok", "OK"]
      },
      timeout: {
        noResponse: `No hemos recibido ninguna respuesta. Contactaremos a sus contactos de emergencia para verificar que está bien.`,
        retry: `No hemos recibido ninguna respuesta. Le llamaremos de nuevo.`,
      },
      responseMessages: {
        ok: `Gracias por confirmar que está bien. Ahora daremos por finalizada la llamada. Cuídese.`,
        notOk: `Gracias por avisarnos. Nos pondremos en contacto de inmediato con sus contactos de emergencia.`,
        emergency: `Gracias por avisarnos. Nos pondremos en contacto de inmediato con sus contactos de emergencia.`,
        noAnswer: `Adiós.`,
        errorGeneric: `Perdón, se produjo un error al procesar su respuesta.`,
        goodbye: `Adiós.`,
        clarification: `No entendí su respuesta. Presione 1 si está bien, 5 si no está bien, o 9 para ayuda. También puede decir "Bien", "no bien" o "Ayuda".`,
        smsClarification: `No entendí su respuesta. Por favor, responda ahora. Responda "Bien" si está bien, "no bien" si no está bien, o "Ayuda" si necesita ayuda.`,
        notUnderstoodFinal: `No entendimos su respuesta. Nos pondremos en contacto con sus contactos de emergencia para verificar cómo está.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `¡Buenas noticias! ${loved} ha completado su control de salud y está bien.`,
          emailTemplate: (loved: string) =>
            `¡Buenas noticias! ${loved} ha completado su control de salud y está bien. Esta es una actualización informativa de Omi-Check.`,
        },
        emergency: {
          greeting: `Hola, esta es una notificación de emergencia de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Te llamamos para informarte que puede haber una situación de emergencia con ${loved}. Por favor, presioná 1 para confirmar que recibiste este mensaje.`,
          prompt: `Por favor, respondé ahora presionando 1 para confirmar la recepción.`,
          timeout: `No recibimos respuesta. Continuaremos intentando contactarte a vos y a otros contactos de emergencia.`,
          smsTemplate: (loved: string) =>
            `${loved} ha indicado que necesita ayuda. Se recomienda encarecidamente intentar contactarlo/a lo más rápido posible. Por favor, respondé "OK" para confirmar que has leído este mensaje.`,
          emailTemplate: (loved: string) =>
            `Notificación de Emergencia de Omi-Check: Puede haber una situación de emergencia con ${loved}. Por favor, verifícalo inmediatamente. Esta es una actualización informativa de Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Hola, esta es una notificación urgente de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Te llamamos para informarte que no pudimos obtener respuesta de ${loved} durante su llamada de verificación. Puede que no pueda responder. Por favor, presioná 1 para confirmar que recibiste este mensaje y verificá su estado lo antes posible.`,
          prompt: `Por favor, respondé ahora presionando 1 para confirmar la recepción.`,
          timeout: `No recibimos respuesta. Continuaremos intentando contactarte a vos y a otros contactos de emergencia.`,
          smsTemplate: (loved: string) =>
            `No pudimos obtener respuesta de ${loved} durante su llamada de verificación. Puede que no pueda responder. Por favor, verificá su estado lo antes posible y respondé "OK" para confirmar que has leído este mensaje.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Notificación Urgente de Omi-Check: No pudimos obtener respuesta de ${loved} durante su llamada de verificación. Puede que no pueda responder. Por favor, verificá su estado lo antes posible. Esta es una actualización informativa de Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Hola, esta es una notificación de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Te llamamos para informarte que ${loved} ha indicado que no se encuentra bien. Por favor, presioná 1 para confirmar que recibiste este mensaje.`,
          prompt: `Por favor, respondé ahora presionando 1 para confirmar la recepción.`,
          timeout: `No recibimos respuesta. Continuaremos intentando contactarte a vos y a otros contactos.`,
          smsTemplate: (loved: string) =>
            `Te llamamos para informarte que ${loved} ha indicado que no se encuentra bien. Por favor, respondé "ok" para confirmar.`,
          emailTemplate: (loved: string) =>
            `Notificación de Omi-Check: ${loved} ha indicado que no se encuentra bien. Por favor, verifícalo cuando sea posible. Esta es una actualización informativa de Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `RECORDATORIO: Notificación de Omi-Check: ${loved} ha indicado que no se encuentra bien. Por favor, respondé "ok" para confirmar.`,
          escalationEmailTemplate: (loved: string) =>
            `URGENTE: Intentamos contactar a todos los contactos de emergencia para ${loved} sin éxito. Nadie respondió a nuestras notificaciones. Por favor, verifícalo inmediatamente. Esta es una notificación de escalación de Omi-Check.`,
        },
        noAnswer: {
          greeting: `Hola, esta es una notificación de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Te llamamos para informarte que no pudimos contactar con ${loved} para su verificación de salud programada. Por favor, presioná 1 para confirmar que recibiste este mensaje.`,
          prompt: `Por favor, respondé ahora presionando 1 para confirmar la recepción.`,
          timeout: `No recibimos respuesta. Continuaremos intentando contactarte a vos y a otros contactos.`,
          smsTemplate: (loved: string) =>
            `Te llamamos para informarte que no pudimos contactar con ${loved} para su verificación de salud programada. Por favor, respondé "ok" para confirmar.`,
          emailTemplate: (loved: string) =>
            `Notificación de Omi-Check: No pudimos contactar con ${loved} para su verificación de salud programada. Por favor, verifícalo cuando sea posible. Esta es una actualización informativa de Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} no respondió. Intentaremos de nuevo en ${retryInMinutes} minutos.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} no respondió a la verificación programada. Intentaremos de nuevo automáticamente en ${retryInMinutes} minutos. Actualización informativa de Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `No pudimos entender la respuesta de ${loved} durante su verificación de salud. Por favor, verifícalo para asegurarte de que está bien, de Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Tu ser querido ${loved}, respondió el teléfono durante su llamada programada pero no pudimos entender su respuesta verbal. Por favor, verifícalo para asegurarte de que está bien.`,
        },
      },
    },
    fr: {
      languageId: 'fr',
      call: {
        greeting: `Bonjour, ceci est un appel de vérification de santé.`,
        prompt: `Appuyez sur 1 si vous allez bien, 5 si vous n'allez pas bien et 9 pour l'aide ou dites "Bien", "pas bien" ou "Aide".`,
        timeout: `Nous n'avons reçu aucune réponse. Nous vous rappellerons.`,
        sms: `Bonjour, ceci est un message de vérification pour savoir comment vous allez. Veuillez répondre à ce message.`,
        smsPrompt: `Répondez "bien", "pas bien" ou "aide" selon votre état. Merci!`,
        meaningsKeyword: {
          // Standalone "bien" is matched only in determineHcResponseMeaning (^bien$); omit here so "\bbien\b"
          // does not fire on carrier receipts like "Message bien reçu".
          ok: [
            "Je vais bien",
            "Je suis bien",
            "Tout va bien",
            "ça va bien",
            "ca va bien",
            "très bien",
            "tres bien",
            "Je me sens bien",
            "Je suis en bonne santé",
            "Je suis en sécurité",
            "Tout est bien",
            "Je me porte bien",
            "Tout va pour le mieux",
            "c'est bien",
            "cest bien",
          ],
          notOk: ["pas bien", "Je ne vais pas bien", "Je suis malade", "J'ai des problèmes", "Je me sens mal", "Je ne suis pas bien", "Je me sens malade", "Je ne me sens pas bien", "J'ai besoin de soins", "Mon état n'est pas bon"],
          needHelp: ["aide", "J'ai besoin d'aide", "Aidez-moi", "Je suis en danger", "Urgence", "J'ai besoin d'aide urgente", "Au secours", "J'ai besoin d'assistance", "Aide immédiate", "SOS"]
        },
        receiptConfirmationKeywords: ["Compris", "D'accord", "Je confirme", "Je comprends", "Oui", "Confirmé", "Reçu", "ok", "OK"]
      },
      timeout: {
        noResponse: `Nous n'avons reçu aucune réponse. Nous contacterons vos contacts d'urgence pour vérifier que vous allez bien.`,
        retry: `Nous n'avons reçu aucune réponse. Nous vous rappellerons.`,
      },
      responseMessages: {
        ok: `Merci d'avoir confirmé que vous allez bien. Nous allons mettre fin à cet appel. Prenez soin de vous!`,
        notOk: `Merci de nous en informer. Nous allons contacter vos contacts d'urgence immédiatement.`,
        emergency: `Merci de nous en informer. Nous allons contacter vos contacts d'urgence immédiatement.`,
        noAnswer: `Au revoir.`,
        errorGeneric: `Désolé, une erreur est survenue lors du traitement de votre réponse.`,
        goodbye: `Au revoir.`,
        clarification: `Je n'ai pas compris votre réponse. Veuillez appuyer sur 1 si vous allez bien, sur 5 si vous ne allez pas bien, ou sur 9 pour l'aide. Vous pouvez aussi dire "Bien", "pas bien" ou "Aide".`,
        smsClarification: `Je n'ai pas compris votre réponse. Veuillez répondre maintenant. Répondez "Bien" si vous allez bien, "pas bien" si vous n'allez pas bien, ou "Aide" si vous avez besoin d'aide.`,
        notUnderstoodFinal: `Nous n'avons pas compris votre réponse. Nous allons contacter vos contacts d'urgence pour prendre de vos nouvelles.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Bonne nouvelle! ${loved} a terminé son bilan de santé et se porte bien.`,
          emailTemplate: (loved: string) =>
            `Bonne nouvelle! ${loved} a terminé son bilan de santé et se porte bien. Ceci est une mise à jour informative de Omi-Check.`,
        },
        emergency: {
          greeting: `Bonjour, ceci est une notification d'urgence de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Nous vous appelons pour vous informer qu'il pourrait y avoir une situation d'urgence avec ${loved}. Veuillez appuyer sur 1 pour confirmer que vous avez reçu ce message.`,
          prompt: `Veuillez répondre maintenant en appuyant sur 1 pour confirmer la réception.`,
          timeout: `Nous n'avons pas reçu de réponse. Nous continuerons d'essayer de vous joindre ainsi que d'autres contacts d'urgence.`,
          smsTemplate: (loved: string) =>
            `${loved} a indiqué avoir besoin d'aide. Il est fortement recommandé de tenter de le/la rejoindre le plus rapidement possible. SVP répondre "OK" pour confirmer que vous avez lu ce message.`,
          emailTemplate: (loved: string) =>
            `Notification d'urgence Omi-Check: Il pourrait y avoir une situation d'urgence avec ${loved}. Veuillez vérifier immédiatement. Ceci est une mise à jour informative de Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Bonjour, ceci est une notification urgente de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Nous vous appelons pour vous informer que nous n'avons pas pu obtenir de réponse de ${loved} lors de leur appel de vérification. Ils sont peut-être dans l'incapacité de répondre. Veuillez appuyer sur 1 pour confirmer la réception et vérifier leur état dès que possible.`,
          prompt: `Veuillez répondre maintenant en appuyant sur 1 pour confirmer la réception.`,
          timeout: `Nous n'avons pas reçu de réponse. Nous continuerons d'essayer de vous joindre ainsi que d'autres contacts d'urgence.`,
          smsTemplate: (loved: string) =>
            `Nous n'avons pas pu obtenir de réponse de ${loved} lors de leur appel de vérification. Ils sont peut-être dans l'incapacité de répondre. Veuillez vérifier leur état dès que possible et répondre « OK » pour confirmer avoir lu ce message.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Notification urgente Omi-Check : Nous n'avons pas pu obtenir de réponse de ${loved} lors de leur appel de vérification. Ils sont peut-être dans l'incapacité de répondre. Veuillez vérifier leur état dès que possible. Ceci est une mise à jour informative d'Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Bonjour, ceci est une notification de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Nous vous appelons pour vous informer que ${loved} a indiqué qu'il ne va pas bien. Veuillez appuyer sur 1 pour confirmer que vous avez reçu ce message.`,
          prompt: `Veuillez répondre maintenant en appuyant sur 1 pour confirmer la réception.`,
          timeout: `Nous n'avons pas reçu de réponse. Nous continuerons d'essayer de vous joindre ainsi que d'autres contacts.`,
          smsTemplate: (loved: string) =>
            `Nous vous appelons pour vous informer que ${loved} a indiqué qu'il ne va pas bien. Veuillez répondre "ok" pour confirmer.`,
          emailTemplate: (loved: string) =>
            `Notification Omi-Check: ${loved} a indiqué qu'il ne va pas bien. Veuillez vérifier quand c'est possible. Ceci est une mise à jour informative de Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `RAPPEL: Notification Omi-Check: ${loved} a indiqué qu'il ne va pas bien. Veuillez répondre "ok" pour confirmer.`,
          escalationEmailTemplate: (loved: string) =>
            `URGENT: Nous avons tenté de contacter tous les contacts d'urgence pour ${loved} sans succès. Personne n'a répondu à nos notifications. Veuillez vérifier immédiatement. Ceci est une notification d'escalade de Omi-Check.`,
        },
        noAnswer: {
          greeting: `Bonjour, ceci est une notification de Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Nous vous appelons pour vous informer que nous n'avons pas pu joindre ${loved} pour leur vérification de santé programmée. Veuillez appuyer sur 1 pour confirmer que vous avez reçu ce message.`,
          prompt: `Veuillez répondre maintenant en appuyant sur 1 pour confirmer la réception.`,
          timeout: `Nous n'avons pas reçu de réponse. Nous continuerons d'essayer de vous joindre ainsi que d'autres contacts.`,
          smsTemplate: (loved: string) =>
            `Nous vous appelons pour vous informer que nous n'avons pas pu joindre ${loved} pour leur vérification de santé programmée. Veuillez répondre "ok" pour confirmer.`,
          emailTemplate: (loved: string) =>
            `Notification Omi-Check: Nous n'avons pas pu joindre ${loved} pour leur vérification de santé programmée. Veuillez vérifier quand c'est possible. Ceci est une mise à jour informative de Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} n'a pas répondu. Nous réessaierons dans ${retryInMinutes} minutes.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check : ${loved} n'a pas répondu à l'appel de vérification prévu. Nous réessaierons automatiquement dans ${retryInMinutes} minutes. Message informatif d'Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `Nous n'avons pas pu comprendre la réponse de ${loved} lors de leur vérification de santé. Veuillez vérifier pour vous assurer qu'ils vont bien, de Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Votre proche ${loved}, a répondu au téléphone lors de son appel programmé mais nous n'avons pas pu comprendre sa réponse verbale. Veuillez vérifier pour vous assurer qu'il va bien.`,
        },
      },
    },
    hi: {
      languageId: 'hi',
      call: {
        greeting: `नमस्ते, यह एक स्वास्थ्य जांच कॉल है।`,
        prompt: `यदि आप ठीक हैं तो 1 दबाएं, यदि आप ठीक नहीं हैं तो 5 दबाएं और मदद के लिए 9 दबाएं या कहें "ठीक", "ठीक नहीं" या "मदद"।`,
        timeout: `हमें कोई जवाब नहीं मिला। हम आपको फिर से कॉल करेंगे।`,
        sms: `नमस्ते, यह एक जांच संदेश है ताकि हम जान सकें कि आप कैसे हैं। कृपया इस संदेश का जवाब दें।`,
        smsPrompt: `"ठीक", "ठीक नहीं" या "मदद" जवाब दें अपनी स्थिति के अनुसार। धन्यवाद!`,
        meaningsKeyword: {
          ok: ["ठीक", "मैं ठीक हूं", "मैं अच्छा हूं", "सब कुछ ठीक है", "मैं अच्छा महसूस कर रहा हूं", "मैं स्वस्थ हूं", "मैं सुरक्षित हूं", "सब ठीक है", "मैं ठीक-ठाक हूं", "मेरी हालत अच्छी है"],
          notOk: ["ठीक नहीं", "मैं ठीक नहीं हूं", "मैं बीमार हूं", "मुझे समस्याएं हैं", "मैं बुरा महसूस कर रहा हूं", "मेरी तबीयत ठीक नहीं है", "मैं अस्वस्थ हूं", "मुझे परेशानी है", "मैं बीमार महसूस कर रहा हूं", "मुझे देखभाल चाहिए"],
          needHelp: ["मदद", "मुझे मदद चाहिए", "मेरी मदद करें", "मुझे सहायता चाहिए", "मैं खतरे में हूं", "आपातकाल", "मुझे तत्काल मदद चाहिए", "बचाओ", "मुझे जरूरत है", "तुरंत मदद"]
        },
        receiptConfirmationKeywords: ["समझ गया", "ठीक है", "पुष्टि करता हूं", "मैं समझ गया", "हां", "पुष्टि", "सुना"]
      },
      timeout: {
        noResponse: `हमें कोई जवाब नहीं मिला। हम आपके आपातकालीन संपर्कों से संपर्क करेंगे ताकि यह सुनिश्चित हो सके कि आप ठीक हैं।`,
        retry: `हमें कोई जवाब नहीं मिला। हम आपको फिर से कॉल करेंगे।`,
      },
      responseMessages: {
        ok: `धन्यवाद, आपने पुष्टि की कि आप ठीक हैं। अब हम इस कॉल को समाप्त करेंगे। अपना ख्याल रखें।`,
        notOk: `जानकारी देने के लिए धन्यवाद। हम तुरंत आपके आपातकालीन संपर्कों से संपर्क करेंगे।`,
        emergency: `जानकारी देने के लिए धन्यवाद। हम तुरंत आपके आपातकालीन संपर्कों से संपर्क करेंगे।`,
        noAnswer: `अलविदा।`,
        errorGeneric: `क्षमा करें, आपके जवाब को संसाधित करते समय एक त्रुटि हुई।`,
        goodbye: `अलविदा।`,
        clarification: `मैं आपका जवाब समझ नहीं सका। कृपया 1 दबाएं यदि आप ठीक हैं, 5 यदि आप ठीक नहीं हैं, या 9 यदि आपको मदद चाहिए। आप "ठीक", "ठीक नहीं", या "मदद" भी कह सकते हैं।`,
        smsClarification: `मैं आपका जवाब समझ नहीं सका। कृपया अब जवाब दें। "ठीक" लिखें यदि आप ठीक हैं, "ठीक नहीं" लिखें यदि आप ठीक नहीं हैं, या "मदद" लिखें यदि आपको मदद चाहिए।`,
        notUnderstoodFinal: `हम आपका जवाब समझ नहीं सके। हम आपके आपातकालीन संपर्कों से संपर्क करेंगे आपकी जांच करने के लिए।`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `अच्छी खबर! ${loved} ने अपनी स्वास्थ्य जांच पूरी कर ली है और वे ठीक हैं।`,
          emailTemplate: (loved: string) =>
            `अच्छी खबर! ${loved} ने अपनी स्वास्थ्य जांच पूरी कर ली है और वे ठीक हैं। यह Omi-Check से एक सूचनात्मक अपडेट है।`,
        },
        emergency: {
          greeting: `नमस्ते, यह Omi-Check से एक आपातकालीन सूचना है।`,
          messageTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि ${loved} के साथ आपातकालीन स्थिति हो सकती है। कृपया इस संदेश को प्राप्त करने की पुष्टि के लिए 1 दबाएं।`,
          prompt: `कृपया अब 1 दबाकर प्राप्ति की पुष्टि करें।`,
          timeout: `हमें कोई जवाब नहीं मिला। हम आपसे और अन्य आपातकालीन संपर्कों से संपर्क करने का प्रयास जारी रखेंगे।`,
          smsTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि ${loved} के साथ आपातकालीन स्थिति हो सकती है। कृपया "ठीक है" लिखकर पुष्टि करें।`,
          emailTemplate: (loved: string) =>
            `Omi-Check आपातकालीन सूचना: ${loved} के साथ आपातकालीन स्थिति हो सकती है। कृपया तुरंत उनकी जांच करें। यह Omi-Check से एक सूचनात्मक अपडेट है।`,
        },
        emergencyNoResponse: {
          greeting: `नमस्ते, यह Omi-Check से एक जरूरी सूचना है।`,
          messageTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि हमें ${loved} से उनकी जांच कॉल के दौरान कोई जवाब नहीं मिला। वे जवाब देने में असमर्थ हो सकते हैं। कृपया इस संदेश की पुष्टि के लिए 1 दबाएं और जल्द से जल्द उनकी जांच करें।`,
          prompt: `कृपया अब 1 दबाकर प्राप्ति की पुष्टि करें।`,
          timeout: `हमें कोई जवाब नहीं मिला। हम आपसे और अन्य आपातकालीन संपर्कों से संपर्क करने का प्रयास जारी रखेंगे।`,
          smsTemplate: (loved: string) =>
            `हमें ${loved} से उनकी जांच कॉल के दौरान कोई जवाब नहीं मिला। वे जवाब देने में असमर्थ हो सकते हैं। कृपया जल्द से जल्द उनकी जांच करें और "ठीक है" लिखकर पुष्टि करें।`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check जरूरी सूचना: हमें ${loved} से उनकी जांच कॉल के दौरान कोई जवाब नहीं मिला। वे जवाब देने में असमर्थ हो सकते हैं। कृपया जल्द से जल्द उनकी जांच करें। यह Omi-Check से एक सूचनात्मक अपडेट है।`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `नमस्ते, यह Omi-Check से एक सूचना है।`,
          messageTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि ${loved} ने संकेत दिया है कि वे ठीक नहीं महसूस कर रहे हैं। कृपया इस संदेश को प्राप्त करने की पुष्टि के लिए 1 दबाएं।`,
          prompt: `कृपया अब 1 दबाकर प्राप्ति की पुष्टि करें।`,
          timeout: `हमें कोई जवाब नहीं मिला। हम आपसे और अन्य संपर्कों से संपर्क करने का प्रयास जारी रखेंगे।`,
          smsTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि ${loved} ने संकेत दिया है कि वे ठीक नहीं महसूस कर रहे हैं। कृपया "ठीक है" लिखकर पुष्टि करें।`,
          emailTemplate: (loved: string) =>
            `Omi-Check सूचना: ${loved} ने संकेत दिया है कि वे ठीक नहीं महसूस कर रहे हैं। कृपया जब संभव हो तो उनकी जांच करें। यह Omi-Check से एक सूचनात्मक अपडेट है।`,
          retrySmsTemplate: (loved: string) =>
            `स्मरण: Omi-Check सूचना: ${loved} ने संकेत दिया है कि वे ठीक नहीं महसूस कर रहे हैं। कृपया "ठीक है" लिखकर पुष्टि करें।`,
          escalationEmailTemplate: (loved: string) =>
            `जरूरी: हमने ${loved} के लिए सभी आपातकालीन संपर्कों से संपर्क करने की कोशिश की लेकिन सफल नहीं हुए। किसी ने भी हमारी सूचनाओं का जवाब नहीं दिया। कृपया तुरंत उनकी जांच करें। यह Omi-Check से एक एस्केलेशन सूचना है।`,
        },
        noAnswer: {
          greeting: `नमस्ते, यह Omi-Check से एक सूचना है।`,
          messageTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि हम ${loved} से उनकी निर्धारित स्वास्थ्य जांच के लिए संपर्क नहीं कर सके। कृपया इस संदेश को प्राप्त करने की पुष्टि के लिए 1 दबाएं।`,
          prompt: `कृपया अब 1 दबाकर प्राप्ति की पुष्टि करें।`,
          timeout: `हमें कोई जवाब नहीं मिला। हम आपसे और अन्य संपर्कों से संपर्क करने का प्रयास जारी रखेंगे।`,
          smsTemplate: (loved: string) =>
            `हम आपको सूचित करने के लिए कॉल कर रहे हैं कि हम ${loved} से उनकी निर्धारित स्वास्थ्य जांच के लिए संपर्क नहीं कर सके। कृपया "ठीक है" लिखकर पुष्टि करें।`,
          emailTemplate: (loved: string) =>
            `Omi-Check सूचना: हम ${loved} से उनकी निर्धारित स्वास्थ्य जांच के लिए संपर्क नहीं कर सके। कृपया जब संभव हो तो उनकी जांच करें। यह Omi-Check से एक सूचनात्मक अपडेट है।`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} ने जवाब नहीं दिया। हम ${retryInMinutes} मिनट में फिर से प्रयास करेंगे।`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} ने निर्धारित स्वास्थ्य जांच कॉल का जवाब नहीं दिया। हम ${retryInMinutes} मिनट में स्वचालित रूप से फिर से प्रयास करेंगे। यह Omi-Check से एक सूचनात्मक अपडेट है।`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `हम ${loved} की स्वास्थ्य जांच के दौरान उनकी प्रतिक्रिया को समझ नहीं सके। कृपया यह सुनिश्चित करने के लिए उनकी जांच करें कि वे ठीक हैं, Omi-Check से।`,
          emailTemplate: (loved: string) =>
            `आपके प्रियजन ${loved}, ने अपने निर्धारित कॉल के दौरान फोन का जवाब दिया लेकिन हम उनके मौखिक जवाब को समझ नहीं सके। कृपया यह सुनिश्चित करने के लिए उनकी जांच करें कि वे ठीक हैं।`,
        },
      },
    },
    he: {
      languageId: 'he',
      call: {
        greeting: `שלום, זהו שיחת בדיקת בריאות.`,
        prompt: `לחץ 1 אם אתה בסדר, 5 אם אינך בסדר ו-9 לעזרה או אמור "בסדר", "לא בסדר" או "עזרה".`,
        timeout: `לא קיבלנו תגובה. נתקשר אליך שוב.`,
        sms: `שלום, זהו הודעת בדיקה כדי שנדע איך אתה מרגיש. אנא הגב להודעה זו.`,
        smsPrompt: `הגב "בסדר", "לא בסדר" או "עזרה" בהתאם למצבך. תודה!`,
        meaningsKeyword: {
          ok: ["בסדר", "אני בסדר", "אני טוב", "הכל בסדר", "אני מרגיש טוב", "אני בריא", "אני בטוח", "הכל תקין", "אני מרגיש מצוין", "אני במצב טוב"],
          notOk: ["לא בסדר", "אני לא בסדר", "אני חולה", "יש לי בעיות", "אני מרגיש רע", "אני לא מרגיש טוב", "בריאותי לא טובה", "מצבי לא טוב", "אני מרגיש חולה", "אני צריך טיפול"],
          needHelp: ["עזרה", "אני צריך עזרה", "עזור לי", "אני בסכנה", "חירום", "אני צריך עזרה דחופה", "הצילו", "אני צריך סיוע", "עזרה מיידית", "SOS"]
        },
        receiptConfirmationKeywords: ["הבנתי", "בסדר", "מאשר", "אני מבין", "כן", "אושר", "שמעתי"]
      },
      timeout: {
        noResponse: `לא קיבלנו תגובה. נצור קשר עם אנשי הקשר החירום שלך כדי לוודא שאתה בסדר.`,
        retry: `לא קיבלנו תגובה. נתקשר אליך שוב.`,
      },
      responseMessages: {
        ok: `תודה שאישרת שאתה בסדר. אנו נסיים כעת את השיחה. שמור על עצמך.`,
        notOk: `תודה שהודעת לנו. ניצור קשר מיידית עם אנשי הקשר לשעת חירום שלך.`,
        emergency: `תודה שהודעת לנו. ניצור קשר מיידית עם אנשי הקשר לשעת חירום שלך.`,
        noAnswer: `להתראות.`,
        errorGeneric: `מצטערים, אירעה שגיאה בעיבוד תגובתך.`,
        goodbye: `להתראות.`,
        clarification: `לא הבנתי את תגובתך. אנא לחץ על 1 אם אתה בסדר, 5 אם אתה לא בסדר, או 9 לעזרה. אתה יכול גם לומר "בסדר", "לא בסדר", או "עזרה".`,
        smsClarification: `לא הבנתי את תגובתך. אנא הגב עכשיו. כתוב "בסדר" אם אתה בסדר, "לא בסדר" אם אתה לא בסדר, או "עזרה" אם אתה צריך עזרה.`,
        notUnderstoodFinal: `לא הבנו את תגובתך. ניצור קשר עם אנשי הקשר לשעת חירום שלך כדי לבדוק מה שלומך.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `חדשות טובות! ${loved} השלים את הבדיקה הבריאותית ומרגיש טוב.`,
          emailTemplate: (loved: string) =>
            `חדשות טובות! ${loved} השלים את הבדיקה הבריאותית ומרגיש טוב. זהו עדכון מידע מ-Omi-Check.`,
        },
        emergency: {
          greeting: `שלום, זו התראות חירום מ-Omi-Check.`,
          messageTemplate: (loved: string) =>
            `אנחנו מתקשרים כדי להודיע לך שייתכן שיש מצב חירום עם ${loved}. אנא לחץ על 1 כדי לאשר שקיבלת את ההודעה הזו.`,
          prompt: `אנא הגב עכשיו על ידי לחיצה על 1 כדי לאשר קבלה.`,
          timeout: `לא קיבלנו תגובה. נמשיך לנסות להגיע אליך ולמגעי חירום אחרים.`,
          smsTemplate: (loved: string) =>
            `אנחנו מתקשרים כדי להודיע לך שייתכן שיש מצב חירום עם ${loved}. אנא הגב "בסדר" לאישור.`,
          emailTemplate: (loved: string) =>
            `התראות חירום Omi-Check: ייתכן שיש מצב חירום עם ${loved}. אנא בדוק אותם מיד. זהו עדכון מידע מ-Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `שלום, זו התראה דחופה מ-Omi-Check.`,
          messageTemplate: (loved: string) =>
            `אנחנו מתקשרים כדי להודיע שלא קיבלנו תגובה מ-${loved} במהלך שיחת הבדיקה שלהם. ייתכן שהם לא מסוגלים להגיב. אנא לחץ על 1 לאישור ובדוק אותם בהקדם האפשרי.`,
          prompt: `אנא הגב עכשיו על ידי לחיצה על 1 כדי לאשר קבלה.`,
          timeout: `לא קיבלנו תגובה. נמשיך לנסות להגיע אליך ולמגעי חירום אחרים.`,
          smsTemplate: (loved: string) =>
            `לא קיבלנו תגובה מ-${loved} במהלך שיחת הבדיקה שלהם. ייתכן שהם לא מסוגלים להגיב. אנא בדוק אותם בהקדם האפשרי והגב "בסדר" לאישור.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `התראה דחופה Omi-Check: לא קיבלנו תגובה מ-${loved} במהלך שיחת הבדיקה שלהם. ייתכן שהם לא מסוגלים להגיב. אנא בדוק אותם בהקדם האפשרי. זהו עדכון מידע מ-Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `שלום, זו התראה מ-Omi-Check.`,
          messageTemplate: (loved: string) =>
            `אנו מתקשרים אליך כדי להודיע לך ש${loved} ציין שהוא לא מרגיש טוב. אנא לחץ על 1 כדי לאשר שקיבלת הודעה זו.`,
          prompt: `אנא הגב עכשיו על ידי לחיצה על 1 כדי לאשר קבלה.`,
          timeout: `לא קיבלנו תגובה. נמשיך לנסות להגיע אליך ולאנשי קשר אחרים.`,
          smsTemplate: (loved: string) =>
            `אנו מתקשרים אליך כדי להודיע לך ש${loved} ציין שהוא לא מרגיש טוב. אנא הגב "בסדר" לאישור.`,
          emailTemplate: (loved: string) =>
            `התראה Omi-Check: ${loved} ציין שהוא לא מרגיש טוב. אנא בדוק אותם כשיש אפשרות. זהו עדכון מידע מ-Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `תזכורת: התראה Omi-Check: ${loved} ציין שהוא לא מרגיש טוב. אנא הגב "בסדר" לאישור.`,
          escalationEmailTemplate: (loved: string) =>
            `דחוף: ניסינו להגיע לכל אנשי הקשר החירום עבור ${loved} ללא הצלחה. איש לא הגיב להתראות שלנו. אנא בדוק אותם מיד. זו התראה של הסלמה מ-Omi-Check.`,
        },
        noAnswer: {
          greeting: `שלום, זו התראה מ-Omi-Check.`,
          messageTemplate: (loved: string) =>
            `אנו מתקשרים אליך כדי להודיע לך שלא הצלחנו ליצור קשר עם ${loved} לבדיקת הבריאות המתוכננת שלהם. אנא לחץ על 1 כדי לאשר שקיבלת הודעה זו.`,
          prompt: `אנא הגב עכשיו על ידי לחיצה על 1 כדי לאשר קבלה.`,
          timeout: `לא קיבלנו תגובה. נמשיך לנסות להגיע אליך ולאנשי קשר אחרים.`,
          smsTemplate: (loved: string) =>
            `אנו מתקשרים אליך כדי להודיע לך שלא הצלחנו ליצור קשר עם ${loved} לבדיקת הבריאות המתוכננת שלהם. אנא הגב "בסדר" לאישור.`,
          emailTemplate: (loved: string) =>
            `התראה Omi-Check: לא הצלחנו ליצור קשר עם ${loved} לבדיקת הבריאות המתוכננת שלהם. אנא בדוק אותם כשיש אפשרות. זהו עדכון מידע מ-Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} לא ענה. ננסה שוב בעוד ${retryInMinutes} דקות.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} לא ענה לשיחת הבדיקה המתוכננת. ננסה שוב אוטומטית בעוד ${retryInMinutes} דקות. עדכון מידע מ-Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `לא הצלחנו להבין את תגובת ${loved} במהלך בדיקת הבריאות שלהם. אנא בדוק אותם כדי לוודא שהם בסדר, מ-Omi-Check.`,
          emailTemplate: (loved: string) =>
            `יקירך ${loved}, ענה לטלפון במהלך השיחה המתוכננת שלו אבל לא הצלחנו להבין את תשובתו המילולית. אנא בדוק אותם כדי לוודא שהם בסדר.`,
        },
      },
    },
    it: {
      languageId: 'it',
      call: {
        greeting: `Ciao, questa è una chiamata di controllo sanitario.`,
        prompt: `Premi 1 se stai bene, 5 se non stai bene e 9 per aiuto o dì "Bene", "non bene" o "Aiuto".`,
        timeout: `Non abbiamo ricevuto alcuna risposta. Ti richiameremo.`,
        sms: `Ciao, questo è un messaggio di controllo per sapere come stai. Per favore rispondi a questo messaggio.`,
        smsPrompt: `Rispondi "bene", "non bene" o "aiuto" a seconda di come stai. Grazie!`,
        meaningsKeyword: {
          ok: ["bene", "Sto bene", "Sono a posto", "Tutto bene", "Mi sento bene", "Sono sano", "Sono al sicuro", "Tutto a posto", "Sto benissimo", "Mi sento in forma"],
          notOk: ["non bene", "Non sto bene", "Sono malato", "Ho problemi", "Mi sento male", "Non mi sento bene", "Sto male", "Mi sento malato", "Ho bisogno di cure", "Il mio stato non è buono"],
          needHelp: ["aiuto", "Ho bisogno di aiuto", "Aiutami", "Sono in pericolo", "Emergenza", "Ho bisogno di aiuto urgente", "Aiuto immediato", "Ho bisogno di assistenza", "Soccorso", "SOS"]
        },
        receiptConfirmationKeywords: ["Capito", "Va bene", "Confermo", "Comprendo", "Sì", "Confermato", "Ricevuto", "ok", "OK"]
      },
      timeout: {
        noResponse: `Non abbiamo ricevuto alcuna risposta. Contatteremo i tuoi contatti di emergenza per verificare che tu stia bene.`,
        retry: `Non abbiamo ricevuto alcuna risposta. Ti richiameremo.`,
      },
      responseMessages: {
        ok: `Grazie per aver confermato che sta bene. Concluderemo ora la chiamata. Si prenda cura di sé!`,
        notOk: `Grazie per avercelo comunicato. Contatteremo immediatamente i suoi contatti di emergenza.`,
        emergency: `Grazie per avercelo comunicato. Contatteremo immediatamente i suoi contatti di emergenza.`,
        noAnswer: `Arrivederci.`,
        errorGeneric: `Spiacenti, si è verificato un errore durante l'elaborazione della sua risposta.`,
        goodbye: `Arrivederci.`,
        clarification: `Non ho capito la sua risposta. Per favore prema 1 se sta bene, 5 se non sta bene, o 9 per aiuto. Può anche dire "Bene", "non bene" o "Aiuto".`,
        smsClarification: `Non ho capito la sua risposta. Per favore, rispondi ora. Rispondi "Bene" se stai bene, "non bene" se non stai bene, o "Aiuto" se hai bisogno di aiuto.`,
        notUnderstoodFinal: `Non abbiamo capito la sua risposta. Contatteremo i suoi contatti di emergenza per verificare come sta.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Buone notizie! ${loved} ha completato il controllo sanitario e sta bene.`,
          emailTemplate: (loved: string) =>
            `Buone notizie! ${loved} ha completato il controllo sanitario e sta bene. Questo è un aggiornamento informativo da Omi-Check.`,
        },
        emergency: {
          greeting: `Ciao, questa è una notifica di emergenza da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Ti stiamo chiamando per informarti che potrebbe esserci una situazione di emergenza con ${loved}. Premi 1 per confermare di aver ricevuto questo messaggio.`,
          prompt: `Rispondi ora premendo 1 per confermare la ricezione.`,
          timeout: `Non abbiamo ricevuto risposta. Continueremo a provare a contattarti e altri contatti di emergenza.`,
          smsTemplate: (loved: string) =>
            `${loved} ha indicato di aver bisogno di aiuto. Si raccomanda vivamente di cercare di contattarlo/a il più rapidamente possibile. Per favore, rispondi "OK" per confermare che hai letto questo messaggio.`,
          emailTemplate: (loved: string) =>
            `Notifica di Emergenza Omi-Check: Potrebbe esserci una situazione di emergenza con ${loved}. Controlla immediatamente. Questo è un aggiornamento informativo da Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Ciao, questa è una notifica urgente da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Ti stiamo chiamando per informarti che non abbiamo ricevuto risposta da ${loved} durante la chiamata di verifica. Potrebbero non essere in grado di rispondere. Premi 1 per confermare e controlla il prima possibile.`,
          prompt: `Rispondi ora premendo 1 per confermare la ricezione.`,
          timeout: `Non abbiamo ricevuto risposta. Continueremo a provare a contattarti e altri contatti di emergenza.`,
          smsTemplate: (loved: string) =>
            `Non abbiamo ricevuto risposta da ${loved} durante la chiamata di verifica. Potrebbero non essere in grado di rispondere. Controlla il prima possibile e rispondi "OK" per confermare.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Notifica Urgente Omi-Check: Non abbiamo ricevuto risposta da ${loved} durante la chiamata di verifica. Potrebbero non essere in grado di rispondere. Controlla il prima possibile. Questo è un aggiornamento informativo da Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Ciao, questa è una notifica da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Ti stiamo chiamando per informarti che ${loved} ha indicato che non sta bene. Premi 1 per confermare di aver ricevuto questo messaggio.`,
          prompt: `Rispondi ora premendo 1 per confermare la ricezione.`,
          timeout: `Non abbiamo ricevuto risposta. Continueremo a provare a contattarti e altri contatti.`,
          smsTemplate: (loved: string) =>
            `Ti stiamo chiamando per informarti che ${loved} ha indicato che non sta bene. Rispondi "Va bene" per confermare.`,
          emailTemplate: (loved: string) =>
            `Notifica Omi-Check: ${loved} ha indicato che non sta bene. Controlla quando possibile. Questo è un aggiornamento informativo da Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `PROMEMORIA: Notifica Omi-Check: ${loved} ha indicato che non sta bene. Rispondi "Va bene" per confermare.`,
          escalationEmailTemplate: (loved: string) =>
            `URGENTE: Abbiamo tentato di contattare tutti i contatti di emergenza per ${loved} senza successo. Nessuno ha risposto alle nostre notifiche. Controlla immediatamente. Questa è una notifica di escalation da Omi-Check.`,
        },
        noAnswer: {
          greeting: `Ciao, questa è una notifica da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Ti stiamo chiamando per informarti che non siamo riusciti a contattare ${loved} per il loro controllo sanitario programmato. Premi 1 per confermare di aver ricevuto questo messaggio.`,
          prompt: `Rispondi ora premendo 1 per confermare la ricezione.`,
          timeout: `Non abbiamo ricevuto risposta. Continueremo a provare a contattarti e altri contatti.`,
          smsTemplate: (loved: string) =>
            `Ti stiamo chiamando per informarti che non siamo riusciti a contattare ${loved} per il loro controllo sanitario programmato. Rispondi "Va bene" per confermare.`,
          emailTemplate: (loved: string) =>
            `Notifica Omi-Check: Non siamo riusciti a contattare ${loved} per il loro controllo sanitario programmato. Controlla quando possibile. Questo è un aggiornamento informativo da Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} non ha risposto. Riproveremo tra ${retryInMinutes} minuti.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} non ha risposto alla chiamata di controllo programmata. Riproveremo automaticamente tra ${retryInMinutes} minuti. Aggiornamento informativo da Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `Non siamo riusciti a capire la risposta di ${loved} durante il loro controllo sanitario. Controlla per assicurarti che stiano bene, da Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Il tuo caro ${loved}, ha risposto al telefono durante la chiamata programmata ma non siamo riusciti a capire la sua risposta verbale. Controlla per assicurarti che stia bene.`,
        },
      },
    },
    ja: {
      languageId: 'ja',
      call: {
        greeting: `こんにちは、これは健康チェック通話です。`,
        prompt: `大丈夫なら1、具合が悪い場合は5、助けが必要な場合は9を押すか、「大丈夫」「具合が悪い」または「助け」と言ってください。`,
        timeout: `返答がありませんでした。再度お電話いたします。`,
        sms: `こんにちは、これはあなたの様子を知るための確認メッセージです。このメッセージに返信してください。`,
        smsPrompt: `あなたの状態に応じて「大丈夫」、「大丈夫ではない」、または「助け」と返信してください。ありがとうございます！`,
        meaningsKeyword: {
          ok: ["大丈夫", "大丈夫です", "元気です", "すべて大丈夫です", "気分が良いです", "健康です", "安全です", "問題ありません", "調子が良いです", "良好です"],
          notOk: ["大丈夫ではない", "大丈夫ではありません", "病気です", "問題があります", "気分が悪いです", "体調が悪いです", "具合が悪いです", "調子が悪いです", "ケアが必要です", "状態が良くないです"],
          needHelp: ["助け", "助けが必要です", "助けてください", "支援が必要です", "危険です", "緊急事態", "緊急の助けが必要です", "すぐに助けて", "緊急支援", "SOS"]
        },
        receiptConfirmationKeywords: ["分かりました", "はい", "確認しました", "理解しました", "はい", "確認済み", "聞きました"]
      },
      timeout: {
        noResponse: `返答がありませんでした。緊急連絡先に連絡して、あなたの安否を確認いたします。`,
        retry: `返答がありませんでした。再度お電話いたします。`,
      },
      responseMessages: {
        ok: `ご無事の確認ありがとうございます。これにて通話を終了いたします。お大事に。`,
        notOk: `お知らせいただきありがとうございます。至急、緊急連絡先へ連絡いたします。`,
        emergency: `お知らせいただきありがとうございます。至急、緊急連絡先へ連絡いたします。`,
        noAnswer: `さようなら。`,
        errorGeneric: `申し訳ありません。応答の処理中にエラーが発生しました。`,
        goodbye: `さようなら。`,
        clarification: `お答えが理解できませんでした。ご無事でしたら1を、具合が悪ければ5を、助けが必要でしたら9を押してください。「大丈夫」、「具合が悪い」、または「助け」と言っていただいても結構です。`,
        smsClarification: `お答えが理解できませんでした。今すぐお答えください。大丈夫でしたら「大丈夫」、具合が悪い場合は「具合が悪い」、助けが必要な場合は「助け」と返信してください。`,
        notUnderstoodFinal: `お答えが理解できませんでした。緊急連絡先に連絡して、お様子を確認いたします。`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `良いお知らせです！${loved}さんの健康チェックが完了し、元気にしています。`,
          emailTemplate: (loved: string) =>
            `良いお知らせです！${loved}さんの健康チェックが完了し、元気にしています。これはOmi-Checkからの情報提供です。`,
        },
        emergency: {
          greeting: `こんにちは、Omi-Checkからの緊急通知です。`,
          messageTemplate: (loved: string) =>
            `お電話いたしますのは、${loved}に緊急事態が発生している可能性があるためです。このメッセージを受信したことを確認するには1を押してください。`,
          prompt: `1を押して受信を確認してください。`,
          timeout: `応答がありませんでした。あなたや他の緊急連絡先への連絡を続けます。`,
          smsTemplate: (loved: string) =>
            `${loved}が助けが必要であることを示しました。できるだけ早く連絡を取ることを強くお勧めします。このメッセージを読んだことを確認するために「OK」と返信してください。`,
          emailTemplate: (loved: string) =>
            `Omi-Check緊急通知：${loved}に緊急事態が発生している可能性があります。すぐに確認してください。これはOmi-Checkからの情報提供です。`,
        },
        emergencyNoResponse: {
          greeting: `こんにちは、Omi-Checkからの緊急通知です。`,
          messageTemplate: (loved: string) =>
            `お電話いたしますのは、${loved}の確認電話中に応答が得られなかったためです。応答できない可能性があります。1を押して確認し、できるだけ早く確認してください。`,
          prompt: `今すぐ1を押して受信を確認してください。`,
          timeout: `応答がありませんでした。あなたや他の緊急連絡先への連絡を続けます。`,
          smsTemplate: (loved: string) =>
            `${loved}の確認電話中に応答が得られませんでした。応答できない可能性があります。できるだけ早く確認し、「OK」と返信して確認してください。`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check緊急通知：${loved}の確認電話中に応答が得られませんでした。応答できない可能性があります。できるだけ早く確認してください。これはOmi-Checkからの情報提供です。`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `こんにちは、Omi-Checkからの通知です。`,
          messageTemplate: (loved: string) =>
            `${loved}が体調が良くないと報告したことをお知らせするためにお電話しています。このメッセージを受信したことを確認するために1を押してください。`,
          prompt: `今すぐ1を押して受信を確認してください。`,
          timeout: `返信がありませんでした。あなたや他の連絡先に連絡を続けます。`,
          smsTemplate: (loved: string) =>
            `${loved}が体調が良くないと報告したことをお知らせするためにお電話しています。「はい」と返信して確認してください。`,
          emailTemplate: (loved: string) =>
            `Omi-Check通知：${loved}が体調が良くないと報告しました。可能な時に確認してください。これはOmi-Checkからの情報提供です。`,
          retrySmsTemplate: (loved: string) =>
            `リマインダー：Omi-Check通知：${loved}が体調が良くないと報告しました。「はい」と返信して確認してください。`,
          escalationEmailTemplate: (loved: string) =>
            `緊急：${loved}のすべての緊急連絡先に連絡を試みましたが成功しませんでした。誰も私たちの通知に応答していません。すぐに確認してください。これはOmi-Checkからのエスカレーション通知です。`,
        },
        noAnswer: {
          greeting: `こんにちは、Omi-Checkからの通知です。`,
          messageTemplate: (loved: string) =>
            `${loved}の予定された健康チェックに連絡できなかったことをお知らせするためにお電話しています。このメッセージを受信したことを確認するために1を押してください。`,
          prompt: `今すぐ1を押して受信を確認してください。`,
          timeout: `返信がありませんでした。あなたや他の連絡先に連絡を続けます。`,
          smsTemplate: (loved: string) =>
            `${loved}の予定された健康チェックに連絡できなかったことをお知らせするためにお電話しています。「はい」と返信して確認してください。`,
          emailTemplate: (loved: string) =>
            `Omi-Check通知：${loved}の予定された健康チェックに連絡できませんでした。可能な時に確認してください。これはOmi-Checkからの情報提供です。`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved}様が応答されませんでした。${retryInMinutes}分後に再試行します。`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check：${loved}様が予定の健康チェック電話に応答されませんでした。${retryInMinutes}分後に自動で再試行します。Omi-Checkからのお知らせです。`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `${loved}の健康チェック中に回答を理解できませんでした。大丈夫かどうか確認してください、Omi-Checkより。`,
          emailTemplate: (loved: string) =>
            `あなたの大切な人${loved}は、予定された電話中に電話に出ましたが、私たちは彼らの口頭での回答を理解できませんでした。大丈夫かどうか確認してください。`,
        },
      },
    },
    ko: {
      languageId: 'ko',
      call: {
        greeting: `안녕하세요, 이것은 건강 확인 전화입니다.`,
        prompt: `괜찮으시면 1, 괜찮지 않으시면 5, 도움이 필요하시면 9를 누르거나 "괜찮아", "괜찮지 않아" 또는 "도움"이라고 말해 주세요.`,
        timeout: `응답을 받지 못했습니다. 다시 전화드리겠습니다.`,
        sms: `안녕하세요, 이것은 여러분이 어떻게 지내고 있는지 알기 위한 확인 메시지입니다. 이 메시지에 답장해 주세요.`,
        smsPrompt: `상태에 따라 "괜찮아", "괜찮지 않아" 또는 "도움"으로 답장해 주세요. 감사합니다!`,
        meaningsKeyword: {
          ok: ["괜찮아", "괜찮습니다", "좋습니다", "모든 것이 괜찮습니다", "기분이 좋습니다", "건강합니다", "안전합니다", "문제없습니다", "컨디션이 좋습니다", "상태가 좋습니다"],
          notOk: ["괜찮지 않아", "괜찮지 않습니다", "아픕니다", "문제가 있습니다", "기분이 나쁩니다", "몸이 안 좋습니다", "컨디션이 나쁩니다", "아픈 것 같습니다", "돌봄이 필요합니다", "상태가 좋지 않습니다"],
          needHelp: ["도움", "도움이 필요합니다", "도와주세요", "지원이 필요합니다", "위험합니다", "응급상황", "긴급 도움이 필요합니다", "구조 요청", "즉시 도움", "SOS"]
        },
        receiptConfirmationKeywords: ["알겠습니다", "네", "확인했습니다", "이해했습니다", "네", "확인됨", "들었습니다"]
      },
      timeout: {
        noResponse: `응답을 받지 못했습니다. 비상 연락처에 연락하여 안전을 확인하겠습니다.`,
        retry: `응답을 받지 못했습니다. 다시 전화드리겠습니다.`,
      },
      responseMessages: {
        ok: `괜찮다고 확인해 주셔서 감사합니다. 이제 전화를 종료하겠습니다. 건강 유의하세요.`,
        notOk: `알려주셔서 감사합니다. 곧바로 비상 연락처에 연락드리겠습니다.`,
        emergency: `알려주셔서 감사합니다. 곧바로 비상 연락처에 연락드리겠습니다.`,
        noAnswer: `안녕히 계세요.`,
        errorGeneric: `죄송합니다. 응답을 처리하는 동안 오류가 발생했습니다.`,
        goodbye: `안녕히 계세요.`,
        clarification: `응답을 이해하지 못했습니다. 괜찮으시면 1번을, 괜찮지 않으시면 5번을, 도움이 필요하시면 9번을 눌러주세요. "괜찮아", "괜찮지 않아" 또는 "도움"이라고 말씀하셔도 됩니다.`,
        smsClarification: `응답을 이해하지 못했습니다. 지금 응답해 주세요. 괜찮으시면 "괜찮아"라고 답장하시고, 괜찮지 않으시면 "괜찮지 않아"라고 답장하시고, 도움이 필요하시면 "도움"이라고 답장해 주세요.`,
        notUnderstoodFinal: `응답을 이해하지 못했습니다. 비상 연락처에 연락하여 상태를 확인하겠습니다.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `좋은 소식입니다! ${loved}님이 건강 체크를 완료했으며 건강합니다.`,
          emailTemplate: (loved: string) =>
            `좋은 소식입니다! ${loved}님이 건강 체크를 완료했으며 건강합니다. 이것은 Omi-Check의 정보 제공 업데이트입니다.`,
        },
        emergency: {
          greeting: `안녕하세요, Omi-Check의 긴급 알림입니다.`,
          messageTemplate: (loved: string) =>
            `연락드리는 이유는 ${loved}에게 응급 상황이 발생했을 가능성이 있기 때문입니다. 이 메시지를 받았다는 것을 확인하려면 1을 누르세요.`,
          prompt: `1을 눌러 수신을 확인하세요.`,
          timeout: `응답을 받지 못했습니다. 귀하와 다른 비상 연락처에게 계속 연락을 시도하겠습니다.`,
          smsTemplate: (loved: string) =>
            `연락드리는 이유는 ${loved}에게 응급 상황이 발생했을 가능성이 있기 때문입니다. "네"라고 답장하여 확인해 주세요.`,
          emailTemplate: (loved: string) =>
            `Omi-Check 응급 알림: ${loved}에게 응급 상황이 발생했을 가능성이 있습니다. 즉시 확인해 주세요. 이것은 Omi-Check의 정보 제공 업데이트입니다.`,
        },
        emergencyNoResponse: {
          greeting: `안녕하세요, Omi-Check의 긴급 알림입니다.`,
          messageTemplate: (loved: string) =>
            `연락드리는 이유는 ${loved}의 확인 전화 중 응답을 받지 못했기 때문입니다. 응답할 수 없을 수 있습니다. 1을 눌러 확인하고 가능한 한 빨리 확인해 주세요.`,
          prompt: `지금 1을 눌러 수신을 확인해 주세요.`,
          timeout: `응답을 받지 못했습니다. 귀하와 다른 비상 연락처에게 계속 연락을 시도하겠습니다.`,
          smsTemplate: (loved: string) =>
            `${loved}의 확인 전화 중 응답을 받지 못했습니다. 응답할 수 없을 수 있습니다. 가능한 한 빨리 확인하고 "네"라고 답장하여 확인해 주세요.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check 긴급 알림: ${loved}의 확인 전화 중 응답을 받지 못했습니다. 응답할 수 없을 수 있습니다. 가능한 한 빨리 확인해 주세요. 이것은 Omi-Check의 정보 제공 업데이트입니다.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `안녕하세요, Omi-Check에서 알림입니다.`,
          messageTemplate: (loved: string) =>
            `${loved}이 기분이 좋지 않다고 표시했다는 것을 알려드리기 위해 전화드리고 있습니다. 이 메시지를 받았다는 것을 확인하려면 1을 누르세요.`,
          prompt: `지금 1을 눌러 수신을 확인해 주세요.`,
          timeout: `응답을 받지 못했습니다. 귀하와 다른 연락처에 계속 연락을 시도하겠습니다.`,
          smsTemplate: (loved: string) =>
            `${loved}이 기분이 좋지 않다고 표시했다는 것을 알려드리기 위해 전화드리고 있습니다. "네"라고 답장하여 확인해 주세요.`,
          emailTemplate: (loved: string) =>
            `Omi-Check 알림: ${loved}이 기분이 좋지 않다고 표시했습니다. 가능할 때 확인해 주세요. 이것은 Omi-Check의 정보 제공 업데이트입니다.`,
          retrySmsTemplate: (loved: string) =>
            `리마인더: Omi-Check 알림: ${loved}이 기분이 좋지 않다고 표시했습니다. "네"라고 답장하여 확인해 주세요.`,
          escalationEmailTemplate: (loved: string) =>
            `긴급: ${loved}의 모든 비상 연락처에 연락을 시도했지만 성공하지 못했습니다. 아무도 우리의 알림에 응답하지 않았습니다. 즉시 확인해 주세요. 이것은 Omi-Check의 에스컬레이션 알림입니다.`,
        },
        noAnswer: {
          greeting: `안녕하세요, Omi-Check에서 알림입니다.`,
          messageTemplate: (loved: string) =>
            `${loved}의 예정된 건강 검진에 연락할 수 없다는 것을 알려드리기 위해 전화드리고 있습니다. 이 메시지를 받았다는 것을 확인하려면 1을 누르세요.`,
          prompt: `지금 1을 눌러 수신을 확인해 주세요.`,
          timeout: `응답을 받지 못했습니다. 귀하와 다른 연락처에 계속 연락을 시도하겠습니다.`,
          smsTemplate: (loved: string) =>
            `${loved}의 예정된 건강 검진에 연락할 수 없다는 것을 알려드리기 위해 전화드리고 있습니다. "네"라고 답장하여 확인해 주세요.`,
          emailTemplate: (loved: string) =>
            `Omi-Check 알림: ${loved}의 예정된 건강 검진에 연락할 수 없었습니다. 가능할 때 확인해 주세요. 이것은 Omi-Check의 정보 제공 업데이트입니다.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved}님이 응답하지 않았습니다. ${retryInMinutes}분 후에 다시 시도합니다.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check 알림: ${loved}님이 예정된 건강 확인 전화에 응답하지 않았습니다. ${retryInMinutes}분 후 자동으로 다시 시도합니다. Omi-Check 정보 안내입니다.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `${loved}의 건강 검진 중 응답을 이해하지 못했습니다. 괜찮은지 확인해 주세요, Omi-Check에서.`,
          emailTemplate: (loved: string) =>
            `귀하의 사랑하는 사람 ${loved}이(가) 예정된 전화 중에 전화를 받았지만 우리는 그들의 구두 답변을 이해할 수 없었습니다. 괜찮은지 확인해 주세요.`,
        },
      },
    },
    pt: {
      languageId: 'pt',
      call: {
        greeting: `Olá, esta é uma chamada de verificação de saúde.`,
        prompt: `Pressione 1 se você está bem, 5 se não está bem e 9 para ajuda ou diga "Bem", "não bem" ou "Ajuda".`,
        timeout: `Não recebemos nenhuma resposta. Ligaremos novamente.`,
        sms: `Olá, esta é uma mensagem de verificação para sabermos como você está. Por favor, responda a esta mensagem.`,
        smsPrompt: `Responda "bem", "não bem" ou "ajuda" dependendo de como você está. Obrigado!`,
        meaningsKeyword: {
          ok: ["bem", "Estou bem", "Estou ok", "Tudo bem", "Me sinto bem", "Estou saudável", "Estou seguro", "Tudo certo", "Estou ótimo", "Me sinto em forma"],
          notOk: ["não bem", "Não estou bem", "Estou doente", "Tenho problemas", "Me sinto mal", "Não me sinto bem", "Estou mal", "Me sinto doente", "Preciso de cuidados", "Meu estado não está bom"],
          needHelp: ["ajuda", "Preciso de ajuda", "Me ajude", "Estou em perigo", "Emergência", "Preciso de ajuda urgente", "Socorro", "Preciso de assistência", "Ajuda imediata", "SOS"]
        },
        receiptConfirmationKeywords: ["Entendi", "Ok", "Confirmo", "Compreendo", "Sim", "Confirmado", "Recebido"]
      },
      timeout: {
        noResponse: `Não recebemos nenhuma resposta. Entraremos em contato com seus contatos de emergência para verificar se você está bem.`,
        retry: `Não recebemos nenhuma resposta. Ligaremos novamente.`,
      },
      responseMessages: {
        ok: `Obrigado por confirmar que você está bem. Vamos encerrar esta chamada agora. Cuide-se!`,
        notOk: `Obrigado por nos informar. Entraremos em contato com seus contatos de emergência imediatamente.`,
        emergency: `Obrigado por nos informar. Entraremos em contato com seus contatos de emergência imediatamente.`,
        noAnswer: `Adeus.`,
        errorGeneric: `Desculpe, ocorreu um erro ao processar sua resposta.`,
        goodbye: `Adeus.`,
        clarification: `Não entendi sua resposta. Por favor pressione 1 se você está bem, 5 se você não está bem, ou 9 para ajuda. Você também pode dizer "Bem", "não bem" ou "Ajuda".`,
        smsClarification: `Não entendi sua resposta. Por favor, responda agora. Responda "Bem" se você está bem, "não bem" se você não está bem, ou "Ajuda" se você precisa de ajuda.`,
        notUnderstoodFinal: `Não entendemos sua resposta. Entraremos em contato com seus contatos de emergência para verificar como você está.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Boas notícias! ${loved} completou seu check-up de saúde e está bem.`,
          emailTemplate: (loved: string) =>
            `Boas notícias! ${loved} completou seu check-up de saúde e está bem. Esta é uma atualização informativa da Omi-Check.`,
        },
        emergency: {
          greeting: `Olá, esta é uma notificação de emergência da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Estamos ligando para informá-lo que pode haver uma situação de emergência com ${loved}. Pressione 1 para confirmar que recebeu esta mensagem.`,
          prompt: `Responda agora pressionando 1 para confirmar o recebimento.`,
          timeout: `Não recebemos resposta. Continuaremos tentando contatá-lo e outros contatos de emergência.`,
          smsTemplate: (loved: string) =>
            `${loved} indicou que precisa de ajuda. É altamente recomendado tentar contatá-lo/a o mais rápido possível. Por favor, responda "OK" para confirmar que você leu esta mensagem.`,
          emailTemplate: (loved: string) =>
            `Notificação de Emergência Omi-Check: Pode haver uma situação de emergência com ${loved}. Verifique imediatamente. Esta é uma atualização informativa da Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Olá, esta é uma notificação urgente da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Estamos ligando para informá-lo que não recebemos resposta de ${loved} durante a chamada de verificação. Eles podem não conseguir responder. Pressione 1 para confirmar e verifique o mais rápido possível.`,
          prompt: `Responda agora pressionando 1 para confirmar o recebimento.`,
          timeout: `Não recebemos resposta. Continuaremos tentando contatá-lo e outros contatos de emergência.`,
          smsTemplate: (loved: string) =>
            `Não recebemos resposta de ${loved} durante a chamada de verificação. Eles podem não conseguir responder. Verifique o mais rápido possível e responda "OK" para confirmar.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Notificação Urgente Omi-Check: Não recebemos resposta de ${loved} durante a chamada de verificação. Eles podem não conseguir responder. Verifique o mais rápido possível. Esta é uma atualização informativa da Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Olá, esta é uma notificação da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Estamos ligando para informá-lo que ${loved} indicou que não está se sentindo bem. Pressione 1 para confirmar que recebeu esta mensagem.`,
          prompt: `Responda agora pressionando 1 para confirmar o recebimento.`,
          timeout: `Não recebemos resposta. Continuaremos tentando contatá-lo e outros contatos.`,
          smsTemplate: (loved: string) =>
            `Estamos ligando para informá-lo que ${loved} indicou que não está se sentindo bem. Responda "Ok" para confirmar.`,
          emailTemplate: (loved: string) =>
            `Notificação Omi-Check: ${loved} indicou que não está se sentindo bem. Verifique quando possível. Esta é uma atualização informativa da Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `LEMBRETE: Notificação Omi-Check: ${loved} indicou que não está se sentindo bem. Responda "Ok" para confirmar.`,
          escalationEmailTemplate: (loved: string) =>
            `URGENTE: Tentamos contatar todos os contatos de emergência para ${loved} sem sucesso. Ninguém respondeu às nossas notificações. Verifique imediatamente. Esta é uma notificação de escalação da Omi-Check.`,
        },
        noAnswer: {
          greeting: `Olá, esta é uma notificação da Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Estamos ligando para informá-lo que não conseguimos contatar ${loved} para sua verificação de saúde programada. Pressione 1 para confirmar que recebeu esta mensagem.`,
          prompt: `Responda agora pressionando 1 para confirmar o recebimento.`,
          timeout: `Não recebemos resposta. Continuaremos tentando contatá-lo e outros contatos.`,
          smsTemplate: (loved: string) =>
            `Estamos ligando para informá-lo que não conseguimos contatar ${loved} para sua verificação de saúde programada. Responda "Ok" para confirmar.`,
          emailTemplate: (loved: string) =>
            `Notificação Omi-Check: Não conseguimos contatar ${loved} para sua verificação de saúde programada. Verifique quando possível. Esta é uma atualização informativa da Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} não atendeu. Tentaremos novamente em ${retryInMinutes} minutos.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} não atendeu à verificação programada. Tentaremos novamente automaticamente em ${retryInMinutes} minutos. Atualização informativa da Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `Não conseguimos entender a resposta de ${loved} durante sua verificação de saúde. Verifique para garantir que estão bem, da Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Seu ente querido ${loved}, atendeu o telefone durante sua chamada agendada, mas não conseguimos entender sua resposta verbal. Verifique para garantir que estão bem.`,
        },
      },
    },
    ru: {
      languageId: 'ru',
      call: {
        greeting: `Здравствуйте, это звонок для проверки здоровья.`,
        prompt: `Нажмите 1, если с вами всё в порядке, 5, если вам плохо, и 9 для помощи, или скажите "В порядке", "не в порядке" или "Помощь".`,
        timeout: `Мы не получили ответа. Мы перезвоним вам.`,
        sms: `Привет, это сообщение для проверки, чтобы мы знали, как у вас дела. Пожалуйста, ответьте на это сообщение.`,
        smsPrompt: `Ответьте "в порядке", "не в порядке" или "помощь" в зависимости от вашего состояния. Спасибо!`,
        meaningsKeyword: {
          ok: ["в порядке", "Я в порядке", "Я хорошо", "Всё в порядке", "Я чувствую себя хорошо", "Я здоров", "Я в безопасности", "Всё хорошо", "Я чувствую себя отлично", "Состояние хорошее"],
          notOk: ["не в порядке", "Я не в порядке", "Мне плохо", "Я болен", "У меня проблемы", "Я чувствую себя плохо", "Я нездоров", "Мне нехорошо", "Мне нужен уход", "Состояние плохое"],
          needHelp: ["помощь", "Мне нужна помощь", "Помогите мне", "Я в опасности", "Чрезвычайная ситуация", "Мне нужна срочная помощь", "Спасите", "Мне нужна помощь немедленно", "Срочная помощь", "SOS"]
        },
        receiptConfirmationKeywords: ["Понял", "Хорошо", "Подтверждаю", "Понимаю", "Да", "Подтверждено", "Услышал"]
      },
      timeout: {
        noResponse: `Мы не получили ответа. Мы свяжемся с вашими контактами для экстренных случаев, чтобы убедиться, что с вами всё в порядке.`,
        retry: `Мы не получили ответа. Мы перезвоним вам.`,
      },
      responseMessages: {
        ok: `Спасибо за подтверждение, что с вами всё в порядке. Мы завершаем звонок. Берегите себя.`,
        notOk: `Спасибо за информацию. Мы немедленно свяжемся с вашими контактами для экстренных случаев.`,
        emergency: `Спасибо за информацию. Мы немедленно свяжемся с вашими контактами для экстренных случаев.`,
        noAnswer: `До свидания.`,
        errorGeneric: `Извините, при обработке вашего ответа произошла ошибка.`,
        goodbye: `До свидания.`,
        clarification: `Я не понял ваш ответ. Пожалуйста, нажмите 1, если с вами всё в порядке, 5, если не в порядке, или 9 для помощи. Вы также можете сказать "В порядке", "не в порядке" или "Помощь".`,
        smsClarification: `Я не понял ваш ответ. Пожалуйста, ответьте сейчас. Ответьте "В порядке", если с вами всё в порядке, "не в порядке", если вам плохо, или "Помощь", если вам нужна помощь.`,
        notUnderstoodFinal: `Мы не поняли ваш ответ. Мы свяжемся с вашими контактами для экстренных случаев, чтобы проверить, как вы себя чувствуете.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Хорошие новости! ${loved} завершил проверку здоровья и чувствует себя хорошо.`,
          emailTemplate: (loved: string) =>
            `Хорошие новости! ${loved} завершил проверку здоровья и чувствует себя хорошо. Это информационное обновление от Omi-Check.`,
        },
        emergency: {
          greeting: `Привет, это экстренное уведомление от Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Мы звоним вам, чтобы сообщить, что с ${loved} может быть чрезвычайная ситуация. Пожалуйста, нажмите 1, чтобы подтвердить получение этого сообщения.`,
          prompt: `Пожалуйста, ответьте сейчас, нажав 1 для подтверждения получения.`,
          timeout: `Мы не получили ответа. Мы продолжим пытаться связаться с вами и другими контактами для экстренных случаев.`,
          smsTemplate: (loved: string) =>
            `${loved} указал(а), что нужна помощь. Настоятельно рекомендуется попытаться связаться с ним/ней как можно скорее. Пожалуйста, ответьте "OK", чтобы подтвердить, что вы прочитали это сообщение.`,
          emailTemplate: (loved: string) =>
            `Уведомление о чрезвычайной ситуации Omi-Check: С ${loved} может быть чрезвычайная ситуация. Пожалуйста, проверьте немедленно. Это информационное обновление от Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Привет, это срочное уведомление от Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Мы звоним вам, чтобы сообщить, что мы не получили ответа от ${loved} во время их проверочного звонка. Они могут быть не в состоянии ответить. Пожалуйста, нажмите 1 для подтверждения и проверьте как можно скорее.`,
          prompt: `Пожалуйста, ответьте сейчас, нажав 1 для подтверждения получения.`,
          timeout: `Мы не получили ответа. Мы продолжим пытаться связаться с вами и другими контактами для экстренных случаев.`,
          smsTemplate: (loved: string) =>
            `Мы не получили ответа от ${loved} во время их проверочного звонка. Они могут быть не в состоянии ответить. Пожалуйста, проверьте как можно скорее и ответьте "Хорошо" для подтверждения.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Срочное уведомление Omi-Check: Мы не получили ответа от ${loved} во время их проверочного звонка. Они могут быть не в состоянии ответить. Пожалуйста, проверьте как можно скорее. Это информационное обновление от Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Привет, это уведомление от Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Мы звоним вам, чтобы сообщить, что ${loved} указал, что он не чувствует себя хорошо. Пожалуйста, нажмите 1, чтобы подтвердить получение этого сообщения.`,
          prompt: `Пожалуйста, ответьте сейчас, нажав 1 для подтверждения получения.`,
          timeout: `Мы не получили ответа. Мы продолжим пытаться связаться с вами и другими контактами.`,
          smsTemplate: (loved: string) =>
            `Мы звоним вам, чтобы сообщить, что ${loved} указал, что он не чувствует себя хорошо. Пожалуйста, ответьте "Хорошо" для подтверждения.`,
          emailTemplate: (loved: string) =>
            `Уведомление Omi-Check: ${loved} указал, что он не чувствует себя хорошо. Пожалуйста, проверьте, когда это возможно. Это информационное обновление от Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `НАПОМИНАНИЕ: Уведомление Omi-Check: ${loved} указал, что он не чувствует себя хорошо. Пожалуйста, ответьте "Хорошо" для подтверждения.`,
          escalationEmailTemplate: (loved: string) =>
            `СРОЧНО: Мы пытались связаться со всеми контактами для экстренных случаев для ${loved} без успеха. Никто не ответил на наши уведомления. Пожалуйста, проверьте немедленно. Это уведомление об эскалации от Omi-Check.`,
        },
        noAnswer: {
          greeting: `Привет, это уведомление от Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Мы звоним вам, чтобы сообщить, что нам не удалось связаться с ${loved} для их запланированной проверки здоровья. Пожалуйста, нажмите 1, чтобы подтвердить получение этого сообщения.`,
          prompt: `Пожалуйста, ответьте сейчас, нажав 1 для подтверждения получения.`,
          timeout: `Мы не получили ответа. Мы продолжим пытаться связаться с вами и другими контактами.`,
          smsTemplate: (loved: string) =>
            `Мы звоним вам, чтобы сообщить, что нам не удалось связаться с ${loved} для их запланированной проверки здоровья. Пожалуйста, ответьте "Хорошо" для подтверждения.`,
          emailTemplate: (loved: string) =>
            `Уведомление Omi-Check: Нам не удалось связаться с ${loved} для их запланированной проверки здоровья. Пожалуйста, проверьте, когда это возможно. Это информационное обновление от Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} не ответил(а). Повторим попытку через ${retryInMinutes} мин.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} не ответил(а) на запланированный звонок проверки. Повторим попытку автоматически через ${retryInMinutes} мин. Информационное сообщение от Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `Нам не удалось понять ответ ${loved} во время их проверки здоровья. Пожалуйста, проверьте, чтобы убедиться, что с ними всё в порядке, от Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Ваш близкий ${loved}, ответил на телефон во время запланированного звонка, но мы не смогли понять его устный ответ. Пожалуйста, проверьте, чтобы убедиться, что с ними всё в порядке.`,
        },
      },
    },
    tr: {
      languageId: 'tr',
      call: {
        greeting: `Merhaba, bu bir sağlık kontrolü aramasıdır.`,
        prompt: `İyiseniz 1'e, iyi değilseniz 5'e ve yardım için 9'a basın veya "İyi", "iyi değil" veya "Yardım" deyin.`,
        timeout: `Hiçbir yanıt almadık. Size tekrar arayacağız.`,
        sms: `Merhaba, bu, nasıl olduğunuzu bilmemiz için bir kontrol mesajıdır. Lütfen bu mesaja yanıt verin.`,
        smsPrompt: `Durumunuza göre "iyi", "iyi değil" veya "yardım" yanıtlayın. Teşekkürler!`,
        meaningsKeyword: {
          ok: ["iyi", "İyiyim", "Her şey yolunda", "İyi hissediyorum", "Sağlıklıyım", "Güvendeyim", "Her şey tamam", "Kendimi iyi hissediyorum", "Durumum iyi", "Fena değilim"],
          notOk: ["iyi değil", "İyi değilim", "Hastayım", "Sorunlarım var", "Kötü hissediyorum", "Kendimi iyi hissetmiyorum", "Hasta hissediyorum", "Durumum iyi değil", "Bakıma ihtiyacım var", "Sağlığım iyi değil"],
          needHelp: ["yardım", "Yardıma ihtiyacım var", "Bana yardım et", "Tehlikedeyim", "Acil durum", "Acil yardıma ihtiyacım var", "İmdat", "Hemen yardım", "Acil destek", "SOS"]
        },
        receiptConfirmationKeywords: ["Anladım", "Tamam", "Onaylıyorum", "Anlıyorum", "Evet", "Onaylandı", "Duydum"]
      },
      timeout: {
        noResponse: `Hiçbir yanıt almadık. İyi olduğunuzdan emin olmak için acil durum kişilerinizle iletişime geçeceğiz.`,
        retry: `Hiçbir yanıt almadık. Size tekrar arayacağız.`,
      },
      responseMessages: {
        ok: `İyi olduğunuzu onayladığınız için teşekkürler. Şimdi bu çağrıyı sonlandıracağız. Kendinize iyi bakın.`,
        notOk: `Bilgi verdiğiniz için teşekkürler. Hemen acil durum kişilerinizle iletişime geçeceğiz.`,
        emergency: `Bilgi verdiğiniz için teşekkürler. Hemen acil durum kişilerinizle iletişime geçeceğiz.`,
        noAnswer: `Hoşça kalın.`,
        errorGeneric: `Üzgünüz, yanıtınız işlenirken bir hata oluştu.`,
        goodbye: `Hoşça kalın.`,
        clarification: `Yanıtınızı anlayamadım. Lütfen iyiyseniz 1'e, iyi değilseniz 5'e, yardıma ihtiyacınız varsa 9'a basın. "İyi", "iyi değil" veya "Yardım" da diyebilirsiniz.`,
        smsClarification: `Yanıtınızı anlayamadım. Lütfen şimdi yanıtlayın. İyiseniz "İyi" yazın, iyi değilseniz "iyi değil" yazın, veya yardıma ihtiyacınız varsa "Yardım" yazın.`,
        notUnderstoodFinal: `Yanıtınızı anlayamadık. Durumunuzu kontrol etmek için acil durum kişilerinizle iletişime geçeceğiz.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `İyi haber! ${loved} sağlık kontrolünü tamamladı ve iyidir.`,
          emailTemplate: (loved: string) =>
            `İyi haber! ${loved} sağlık kontrolünü tamamladı ve iyidir. Bu Omi-Check'dan bilgilendirici bir güncellemedir.`,
        },
        emergency: {
          greeting: `Merhaba, bu Omi-Check'dan acil durum bildirimidir.`,
          messageTemplate: (loved: string) =>
            `Sizi ${loved} acil bir durum olabileceğini bildirmek için arıyoruz. Lütfen bu mesajı aldığınızı onaylamak için 1'e basın.`,
          prompt: `Lütfen şimdi alındıyı onaylamak için 1'e basarak yanıtlayın.`,
          timeout: `Yanıt almadık. Sizinle ve diğer acil durum kişileriyle iletişim kurmaya devam edeceğiz.`,
          smsTemplate: (loved: string) =>
            `Sizi ${loved} acil bir durum olabileceğini bildirmek için arıyoruz. Lütfen "Tamam" yazarak onaylayın.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Acil Durum Bildirimi: ${loved} ile acil bir durum olabilir. Lütfen hemen kontrol edin. Bu Omi-Check'dan bilgilendirici bir güncellemedir.`,
        },
        emergencyNoResponse: {
          greeting: `Merhaba, bu Omi-Check'dan acil bir bildirimdir.`,
          messageTemplate: (loved: string) =>
            `Sizi ${loved} kontrol araması sırasında yanıt alamadığımızı bildirmek için arıyoruz. Yanıt veremiyor olabilirler. Lütfen 1'e basarak onaylayın ve en kısa sürede kontrol edin.`,
          prompt: `Lütfen şimdi alındıyı onaylamak için 1'e basarak yanıtlayın.`,
          timeout: `Yanıt almadık. Sizinle ve diğer acil durum kişileriyle iletişim kurmaya devam edeceğiz.`,
          smsTemplate: (loved: string) =>
            `${loved} kontrol araması sırasında yanıt alamadık. Yanıt veremiyor olabilirler. Lütfen en kısa sürede kontrol edin ve "Tamam" yazarak onaylayın.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check Acil Bildirim: ${loved} kontrol araması sırasında yanıt alamadık. Yanıt veremiyor olabilirler. Lütfen en kısa sürede kontrol edin. Bu Omi-Check'dan bilgilendirici bir güncellemedir.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Merhaba, bu Omi-Check'dan bir bildirimdir.`,
          messageTemplate: (loved: string) =>
            `${loved} iyi hissetmediğini bildirmek için sizi arıyoruz. Bu mesajı aldığınızı onaylamak için lütfen 1'e basın.`,
          prompt: `Lütfen şimdi alındıyı onaylamak için 1'e basarak yanıtlayın.`,
          timeout: `Yanıt almadık. Sizinle ve diğer acil durum kişileriyle iletişim kurmaya devam edeceğiz.`,
          smsTemplate: (loved: string) =>
            `${loved} iyi hissetmediğini bildirmek için sizi arıyoruz. Lütfen "Tamam" yazarak onaylayın.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Bildirimi: ${loved} iyi hissetmediğini bildirdi. Lütfen mümkün olduğunda kontrol edin. Bu Omi-Check'dan bilgilendirici bir güncellemedir.`,
          retrySmsTemplate: (loved: string) =>
            `HATIRLATMA: Omi-Check Bildirimi: ${loved} iyi hissetmediğini bildirdi. Lütfen "Tamam" yazarak onaylayın.`,
          escalationEmailTemplate: (loved: string) =>
            `ACİL: ${loved} için tüm acil durum kişileriyle iletişim kurmaya çalıştık ancak başarılı olamadık. Kimse bildirimlerimize yanıt vermedi. Lütfen hemen kontrol edin. Bu Omi-Check'dan bir eskalasyon bildirimidir.`,
        },
        noAnswer: {
          greeting: `Merhaba, bu Omi-Check'dan bir bildirimdir.`,
          messageTemplate: (loved: string) =>
            `${loved} planlanan sağlık kontrolü için ulaşamadığımızı bildirmek için sizi arıyoruz. Bu mesajı aldığınızı onaylamak için lütfen 1'e basın.`,
          prompt: `Lütfen şimdi alındıyı onaylamak için 1'e basarak yanıtlayın.`,
          timeout: `Yanıt almadık. Sizinle ve diğer acil durum kişileriyle iletişim kurmaya devam edeceğiz.`,
          smsTemplate: (loved: string) =>
            `${loved} planlanan sağlık kontrolü için ulaşamadığımızı bildirmek için sizi arıyoruz. Lütfen "Tamam" yazarak onaylayın.`,
          emailTemplate: (loved: string) =>
            `Omi-Check Bildirimi: ${loved} planlanan sağlık kontrolü için ulaşamadık. Lütfen mümkün olduğunda kontrol edin. Bu Omi-Check'dan bilgilendirici bir güncellemedir.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} yanıt vermedi. ${retryInMinutes} dakika sonra tekrar deneyeceğiz.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} planlanan kontrol aramasına yanıt vermedi. ${retryInMinutes} dakika sonra otomatik olarak tekrar deneyeceğiz. Bilgilendirme mesajı — Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `${loved} sağlık kontrolü sırasında yanıtını anlayamadık. Lütfen iyi olduklarından emin olmak için kontrol edin, Omi-Check'dan.`,
          emailTemplate: (loved: string) =>
            `Sevdiğiniz kişi ${loved}, planlanan arama sırasında telefona cevap verdi ancak sözlü cevabını anlayamadık. Lütfen iyi olduklarından emin olmak için kontrol edin.`,
        },
      },
    },
    vi: {
      languageId: 'vi',
      call: {
        greeting: `Xin chào, đây là cuộc gọi kiểm tra sức khỏe.`,
        prompt: `Nhấn 1 nếu bạn ổn, 5 nếu bạn không ổn và 9 để được giúp đỡ hoặc nói "Ổn", "không ổn" hoặc "Giúp đỡ".`,
        timeout: `Chúng tôi không nhận được phản hồi nào. Chúng tôi sẽ gọi lại cho bạn.`,
        sms: `Xin chào, đây là tin nhắn kiểm tra để chúng tôi biết bạn đang như thế nào. Vui lòng trả lời tin nhắn này.`,
        smsPrompt: `Trả lời "ổn", "không ổn" hoặc "giúp đỡ" tùy theo tình trạng của bạn. Cảm ơn bạn!`,
        meaningsKeyword: {
          ok: ["ổn", "Tôi ổn", "Tôi khỏe", "Mọi thứ đều ổn", "Tôi cảm thấy tốt", "Tôi khỏe mạnh", "Tôi an toàn", "Tất cả đều ổn", "Tôi cảm thấy tuyệt vời", "Tình trạng của tôi tốt"],
          notOk: ["không ổn", "Tôi không ổn", "Tôi không khỏe", "Tôi bị ốm", "Tôi có vấn đề", "Tôi cảm thấy không tốt", "Tôi cảm thấy ốm", "Tình trạng của tôi không tốt", "Tôi cần được chăm sóc", "Sức khỏe của tôi không ổn"],
          needHelp: ["giúp đỡ", "Tôi cần giúp đỡ", "Giúp tôi", "Tôi đang gặp nguy hiểm", "Khẩn cấp", "Tôi cần giúp đỡ khẩn cấp", "Cứu tôi", "Tôi cần hỗ trợ", "Giúp đỡ ngay lập tức", "SOS"]
        },
        receiptConfirmationKeywords: ["Đã hiểu", "Được rồi", "Xác nhận", "Tôi hiểu", "Có", "Đã xác nhận", "Đã nghe"]
      },
      timeout: {
        noResponse: `Chúng tôi không nhận được phản hồi nào. Chúng tôi sẽ liên hệ với các liên hệ khẩn cấp của bạn để đảm bảo bạn ổn.`,
        retry: `Chúng tôi không nhận được phản hồi nào. Chúng tôi sẽ gọi lại cho bạn.`,
      },
      responseMessages: {
        ok: `Cảm ơn bạn đã xác nhận bạn ổn. Chúng tôi sẽ kết thúc cuộc gọi ngay bây giờ. Giữ gìn sức khỏe.`,
        notOk: `Cảm ơn bạn đã thông báo. Chúng tôi sẽ liên hệ ngay với các liên hệ khẩn cấp của bạn.`,
        emergency: `Cảm ơn bạn đã thông báo. Chúng tôi sẽ liên hệ ngay với các liên hệ khẩn cấp của bạn.`,
        noAnswer: `Tạm biệt.`,
        errorGeneric: `Xin lỗi, đã xảy ra lỗi khi xử lý phản hồi của bạn.`,
        goodbye: `Tạm biệt.`,
        clarification: `Tôi không hiểu câu trả lời của bạn. Vui lòng nhấn 1 nếu bạn ổn, 5 nếu bạn không ổn, hoặc 9 để được giúp đỡ. Bạn cũng có thể nói "Ổn", "không ổn", hoặc "Giúp đỡ".`,
        smsClarification: `Tôi không hiểu câu trả lời của bạn. Vui lòng trả lời ngay bây giờ. Trả lời "Ổn" nếu bạn ổn, "không ổn" nếu bạn không ổn, hoặc "Giúp đỡ" nếu bạn cần giúp đỡ.`,
        notUnderstoodFinal: `Chúng tôi không hiểu câu trả lời của bạn. Chúng tôi sẽ liên hệ với các liên hệ khẩn cấp của bạn để kiểm tra tình trạng của bạn.`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `Tin tốt! ${loved} đã hoàn thành kiểm tra sức khỏe và đang khỏe mạnh.`,
          emailTemplate: (loved: string) =>
            `Tin tốt! ${loved} đã hoàn thành kiểm tra sức khỏe và đang khỏe mạnh. Đây là cập nhật thông tin từ Omi-Check.`,
        },
        emergency: {
          greeting: `Xin chào, đây là thông báo khẩn cấp từ Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng có thể có tình huống khẩn cấp với ${loved}. Vui lòng nhấn 1 để xác nhận bạn đã nhận được tin nhắn này.`,
          prompt: `Vui lòng trả lời ngay bằng cách nhấn 1 để xác nhận việc nhận được.`,
          timeout: `Chúng tôi không nhận được phản hồi. Chúng tôi sẽ tiếp tục cố gắng liên lạc với bạn và các liên hệ khẩn cấp khác.`,
          smsTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng có thể có tình huống khẩn cấp với ${loved}. Vui lòng trả lời "Có" để xác nhận.`,
          emailTemplate: (loved: string) =>
            `Thông báo khẩn cấp Omi-Check: Có thể có tình huống khẩn cấp với ${loved}. Vui lòng kiểm tra ngay lập tức. Đây là cập nhật thông tin từ Omi-Check.`,
        },
        emergencyNoResponse: {
          greeting: `Xin chào, đây là thông báo khẩn cấp từ Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng chúng tôi không nhận được phản hồi từ ${loved} trong cuộc gọi kiểm tra. Họ có thể không thể phản hồi. Vui lòng nhấn 1 để xác nhận và kiểm tra càng sớm càng tốt.`,
          prompt: `Vui lòng trả lời ngay bằng cách nhấn 1 để xác nhận việc nhận được.`,
          timeout: `Chúng tôi không nhận được phản hồi. Chúng tôi sẽ tiếp tục cố gắng liên lạc với bạn và các liên hệ khẩn cấp khác.`,
          smsTemplate: (loved: string) =>
            `Chúng tôi không nhận được phản hồi từ ${loved} trong cuộc gọi kiểm tra. Họ có thể không thể phản hồi. Vui lòng kiểm tra càng sớm càng tốt và trả lời "Có" để xác nhận.`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Thông báo khẩn cấp Omi-Check: Chúng tôi không nhận được phản hồi từ ${loved} trong cuộc gọi kiểm tra. Họ có thể không thể phản hồi. Vui lòng kiểm tra càng sớm càng tốt. Đây là cập nhật thông tin từ Omi-Check.`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `Xin chào, đây là thông báo từ Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng ${loved} đã chỉ ra rằng họ không cảm thấy tốt. Vui lòng nhấn 1 để xác nhận bạn đã nhận được tin nhắn này.`,
          prompt: `Vui lòng trả lời ngay bằng cách nhấn 1 để xác nhận việc nhận được.`,
          timeout: `Chúng tôi không nhận được phản hồi. Chúng tôi sẽ tiếp tục cố gắng liên lạc với bạn và các liên hệ khác.`,
          smsTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng ${loved} đã chỉ ra rằng họ không cảm thấy tốt. Vui lòng trả lời "Có" để xác nhận.`,
          emailTemplate: (loved: string) =>
            `Thông báo Omi-Check: ${loved} đã chỉ ra rằng họ không cảm thấy tốt. Vui lòng kiểm tra khi có thể. Đây là cập nhật thông tin từ Omi-Check.`,
          retrySmsTemplate: (loved: string) =>
            `NHẮC NHỞ: Thông báo Omi-Check: ${loved} đã chỉ ra rằng họ không cảm thấy tốt. Vui lòng trả lời "Có" để xác nhận.`,
          escalationEmailTemplate: (loved: string) =>
            `KHẨN CẤP: Chúng tôi đã cố gắng liên lạc với tất cả các liên hệ khẩn cấp cho ${loved} nhưng không thành công. Không ai phản hồi các thông báo của chúng tôi. Vui lòng kiểm tra ngay lập tức. Đây là thông báo leo thang từ Omi-Check.`,
        },
        noAnswer: {
          greeting: `Xin chào, đây là thông báo từ Omi-Check.`,
          messageTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng chúng tôi không thể liên lạc với ${loved} để kiểm tra sức khỏe theo lịch trình. Vui lòng nhấn 1 để xác nhận bạn đã nhận được tin nhắn này.`,
          prompt: `Vui lòng trả lời ngay bằng cách nhấn 1 để xác nhận việc nhận được.`,
          timeout: `Chúng tôi không nhận được phản hồi. Chúng tôi sẽ tiếp tục cố gắng liên lạc với bạn và các liên hệ khác.`,
          smsTemplate: (loved: string) =>
            `Chúng tôi đang gọi điện để thông báo rằng chúng tôi không thể liên lạc với ${loved} để kiểm tra sức khỏe theo lịch trình. Vui lòng trả lời "Có" để xác nhận.`,
          emailTemplate: (loved: string) =>
            `Thông báo Omi-Check: Chúng tôi không thể liên lạc với ${loved} để kiểm tra sức khỏe theo lịch trình. Vui lòng kiểm tra khi có thể. Đây là cập nhật thông tin từ Omi-Check.`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved} chưa trả lời. Chúng tôi sẽ thử lại sau ${retryInMinutes} phút.`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check: ${loved} chưa trả lời cuộc gọi kiểm tra đã lên lịch. Chúng tôi sẽ tự động thử lại sau ${retryInMinutes} phút. Thông tin từ Omi-Check.`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `Chúng tôi không thể hiểu phản hồi của ${loved} trong quá trình kiểm tra sức khỏe. Vui lòng kiểm tra để đảm bảo họ ổn, từ Omi-Check.`,
          emailTemplate: (loved: string) =>
            `Người thân yêu của bạn ${loved}, đã trả lời điện thoại trong cuộc gọi đã lên lịch nhưng chúng tôi không thể hiểu câu trả lời bằng lời nói của họ. Vui lòng kiểm tra để đảm bảo họ ổn.`,
        },
      },
    },
    zh: {
      languageId: 'zh',
      call: {
        greeting: `您好，这是健康检查电话。`,
        prompt: `如果您没事按1，如果您不舒服按5，需要帮助按9，或说"没事"、"不舒服"或"帮助"。`,
        timeout: `我们没有收到任何回应。我们将再次致电给您。`,
        sms: `您好，这是一条检查消息，以便我们了解您的情况。请回复此消息。`,
        smsPrompt: `根据您的情况回复"没事"、"不舒服"或"帮助"。谢谢！`,
        meaningsKeyword: {
          ok: ["没事", "我没事", "我很好", "一切都好", "我感觉很好", "我很健康", "我很安全", "一切正常", "我感觉很棒", "状态良好"],
          notOk: ["不舒服", "我不舒服", "我不好", "我生病了", "我有问题", "我感觉不好", "我身体不适", "我感觉不适", "我需要照顾", "状态不好"],
          needHelp: ["帮助", "我需要帮助", "帮助我", "我有危险", "紧急情况", "我需要紧急帮助", "救命", "我需要立即帮助", "紧急支援", "SOS"]
        },
        receiptConfirmationKeywords: ["明白了", "好的", "确认", "我理解", "是的", "已确认", "听到了"]
      },
      timeout: {
        noResponse: `我们没有收到任何回应。我们将联系您的紧急联系人以确认您的安全。`,
        retry: `我们没有收到任何回应。我们将再次致电给您。`,
      },
      responseMessages: {
        ok: `感谢您确认您一切安好。我们现在将结束通话。请多保重！`,
        notOk: `感谢您的告知。我们将立即联系您的紧急联系人。`,
        emergency: `感谢您的告知。我们将立即联系您的紧急联系人。`,
        noAnswer: `再见。`,
        errorGeneric: `抱歉，处理您的回复时发生错误。`,
        goodbye: `再见。`,
        clarification: `我没有理解您的回答。如果您没事按1，如果您不舒服按5，需要帮助按9。您也可以说"没事"、"不舒服"或"帮助"。`,
        smsClarification: `我没有理解您的回答。请现在回应。回复"没事"如果您没事，回复"不舒服"如果您不舒服，或回复"帮助"如果您需要帮助。`,
        notUnderstoodFinal: `我们没有理解您的回答。我们将联系您的紧急联系人以检查您的状况。`,
      },
      notificationMessages: {
        ok: {
          smsTemplate: (loved: string) =>
            `好消息！${loved}已完成健康检查，一切安好。`,
          emailTemplate: (loved: string) =>
            `好消息！${loved}已完成健康检查，一切安好。这是来自Omi-Check的信息更新。`,
        },
        emergency: {
          greeting: `您好，这是来自Omi-Check的紧急通知。`,
          messageTemplate: (loved: string) =>
            `我们正在致电通知您，${loved}可能遇到紧急情况。请按1确认您已收到此消息。`,
          prompt: `请立即按1确认收到。`,
          timeout: `我们没有收到回复。我们将继续尝试联系您和其他紧急联系人。`,
          smsTemplate: (loved: string) =>
            `Omi-Check通知：${loved}可能遇到紧急情况。请回复"是的"确认收到。`,
          emailTemplate: (loved: string) =>
            `Omi-Check紧急通知：${loved}可能遇到紧急情况。请立即检查。这是来自Omi-Check的信息更新。`,
        },
        emergencyNoResponse: {
          greeting: `您好，这是来自Omi-Check的紧急通知。`,
          messageTemplate: (loved: string) =>
            `我们正在致电通知您，我们在${loved}的检查电话中未收到回复。他们可能无法回应。请按1确认并尽快检查。`,
          prompt: `请立即按1确认收到。`,
          timeout: `我们没有收到回复。我们将继续尝试联系您和其他紧急联系人。`,
          smsTemplate: (loved: string) =>
            `我们在${loved}的检查电话中未收到回复。他们可能无法回应。请尽快检查并回复"是的"确认收到。`,
          emailTemplate: (loved: string, attemptsSummary?: string) => {
            const main = `Omi-Check紧急通知：我们在${loved}的检查电话中未收到回复。他们可能无法回应。请尽快检查。这是来自Omi-Check的信息更新。`;
            return attemptsSummary ? `${main}\n\n${attemptsSummary}` : main;
          },
        },
        notOk: {
          greeting: `您好，这是来自Omi-Check的通知。`,
          messageTemplate: (loved: string) =>
            `我们正在致电通知您，${loved}表示他们感觉不太好。请按1确认您已收到此消息。`,
          prompt: `请立即按1确认收到。`,
          timeout: `我们没有收到回复。我们将继续尝试联系您和其他联系人。`,
          smsTemplate: (loved: string) =>
            `Omi-Check通知：${loved}感觉不太好。请回复"是的"确认收到。`,
          emailTemplate: (loved: string) =>
            `Omi-Check通知：${loved}感觉不太好。请在可能时检查。这是来自Omi-Check的信息更新。`,
          retrySmsTemplate: (loved: string) =>
            `提醒：Omi-Check通知：${loved}感觉不太好。请回复"是的"确认收到。`,
          escalationEmailTemplate: (loved: string) =>
            `紧急：我们尝试联系${loved}的所有紧急联系人，但未成功。没有人回复我们的通知。请立即检查。这是来自Omi-Check的升级通知。`,
        },
        noAnswer: {
          greeting: `您好，这是来自Omi-Check的通知。`,
          messageTemplate: (loved: string) =>
            `我们正在致电通知您，我们无法联系到${loved}进行预定的健康检查。请按1确认您已收到此消息。`,
          prompt: `请立即按1确认收到。`,
          timeout: `我们没有收到回复。我们将继续尝试联系您和其他联系人。`,
          smsTemplate: (loved: string) =>
            `Omi-Check通知：无法联系到${loved}进行健康检查。请回复"是的"确认收到。`,
          emailTemplate: (loved: string) =>
            `Omi-Check通知：无法联系到${loved}进行健康检查。请在可能时检查。这是来自Omi-Check的信息更新。`,
        },
        noAnswerRetryPending: {
          smsTemplate: (loved: string, retryInMinutes: number) =>
            `${loved}未接听。我们将在${retryInMinutes}分钟后重试。`,
          emailTemplate: (loved: string, retryInMinutes: number) =>
            `Omi-Check通知：${loved}未接听预定的健康检查电话。我们将在${retryInMinutes}分钟后自动重试。这是来自Omi-Check的信息更新。`,
        },
        notUnderstood: {
          smsTemplate: (loved: string) =>
            `我们无法理解${loved}在健康检查期间的回复。请检查以确保他们没事，来自Omi-Check。`,
          emailTemplate: (loved: string) =>
            `您的亲人${loved}，在预定的通话中接了电话，但我们无法理解他们的口头回答。请检查以确保他们没事。`,
        },
      },
    },
  };

  /**
   * Maps API / UI locale ids (e.g. fr-CA, ar-SA) to keys present in {@link HcMessageStrings.messages}.
   * Prevents falling back to English when only the base language is defined (fr, ar, …).
   */
  private static resolveMessagesLanguageKey(languageId: string | null | undefined): string {
    const table = this.messages;
    if (languageId == null || String(languageId).trim() === '') {
      return 'en';
    }
    const id = String(languageId).trim();
    if (table[id]) {
      return id;
    }
    const base = id.split(/[-_]/)[0]?.toLowerCase() ?? '';
    if (base && table[base]) {
      return base;
    }
    return 'en';
  }

  // Get complete message set for a language
  static getMessages(languageId: string): HcMessages {
    const key = this.resolveMessagesLanguageKey(languageId);
    return this.messages[key] || this.messages['en'];
  }

  // Get call messages
  static getCallMessages(languageId: string) {
    return this.getMessages(languageId).call;
  }

  /**
   * True if the string is empty/whitespace or exactly matches the built-in voice-call greeting
   * for any supported language. Used when the loved-one language changes so e.g. English default
   * is replaced with the new language default instead of being treated as a "custom" greeting.
   */
  static isBuiltInVoiceCallGreeting(text: string | null | undefined): boolean {
    if (text == null || String(text).trim() === '') {
      return true;
    }
    const t = String(text);
    const table = this.messages;
    for (const code of SUPPORTED_LANGUAGE_CODES) {
      if (table[code]?.call.greeting === t) {
        return true;
      }
    }
    return false;
  }

  /** Same as {@link isBuiltInVoiceCallGreeting} for SMS opener text (`call.sms`). */
  static isBuiltInCallSmsGreeting(text: string | null | undefined): boolean {
    if (text == null || String(text).trim() === '') {
      return true;
    }
    const t = String(text);
    const table = this.messages;
    for (const code of SUPPORTED_LANGUAGE_CODES) {
      if (table[code]?.call.sms === t) {
        return true;
      }
    }
    return false;
  }

  // Get timeout messages
  static getTimeoutMessages(languageId: string) {
    return this.getMessages(languageId).timeout;
  }

  // Get response messages
  static getResponseMessages(languageId: string) {
    return this.getMessages(languageId).responseMessages;
  }

  // Get notification messages (emergencyNoResponse falls back to English when not defined for a language)
  static getNotificationMessages(languageId: string) {
    const messages = this.getMessages(languageId).notificationMessages;
    const enNotifications = this.getMessages('en').notificationMessages;
    const emergencyNoResponse =
      messages.emergencyNoResponse ?? enNotifications.emergencyNoResponse;
    const noAnswerRetryPending =
      messages.noAnswerRetryPending ?? enNotifications.noAnswerRetryPending;
    return { ...messages, emergencyNoResponse, noAnswerRetryPending };
  }

  // Legacy methods for backward compatibility
  static HcStandardResponses = class {
    static get(languageId: string): HcResponseMessages {
      return HcMessageStrings.getResponseMessages(languageId);
    }
  };

  static HcVoice = class {
    static getEmergency(languageId: string, lovedOneName?: string): HcVoiceInstructions {
      const base =
        HcMessageStrings.getNotificationMessages(languageId).emergency;
      const loved = lovedOneName || 'your loved one';
      return {
        greeting: base.greeting,
        message: base.messageTemplate(loved),
        prompt: base.prompt,
        timeout: base.timeout,
        languageId: languageId,
        sms: base.smsTemplate(loved),
      };
    }

    static getEmergencyNoResponse(languageId: string, lovedOneName?: string): HcVoiceInstructions {
      const base =
        HcMessageStrings.getNotificationMessages(languageId).emergencyNoResponse;
      const loved = lovedOneName || 'your loved one';
      if (!base) {
        return HcMessageStrings.HcVoice.getEmergency(languageId, lovedOneName);
      }
      return {
        greeting: base.greeting,
        message: base.messageTemplate(loved),
        prompt: base.prompt,
        timeout: base.timeout,
        languageId: languageId,
        sms: base.smsTemplate(loved),
      };
    }

    static getNotOk(languageId: string, lovedOneName?: string): HcVoiceInstructions {
      const base = HcMessageStrings.getNotificationMessages(languageId).notOk;
      const loved = lovedOneName || 'your loved one';
      return {
        greeting: base.greeting,
        message: base.messageTemplate(loved),
        prompt: base.prompt,
        timeout: base.timeout,
        languageId: languageId,
        sms: base.smsTemplate(loved),
      };
    }

    static getNoAnswer(languageId: string, lovedOneName?: string): HcVoiceInstructions {
      const base =
        HcMessageStrings.getNotificationMessages(languageId).noAnswer;
      const loved = lovedOneName || 'your loved one';
      return {
        greeting: base.greeting,
        message: base.messageTemplate(loved),
        prompt: base.prompt,
        timeout: base.timeout,
        languageId: languageId,
        sms: base.smsTemplate(loved),
      };
    }
  };
}
